import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { Slider } from '../../components/ui/slider';
import { InfoBanner } from '../../components/ui/info-banner';
import { 
  Briefcase, Plus, Loader2, Trash2, Edit, Eye, Building2, Calendar, MapPin, Users, User,
  CheckCircle, Clock, XCircle, X, FileText, CalendarDays, ClipboardList, AlertCircle,
  Upload, Download, FileIcon, Image, File, Archive, RotateCcw, Percent, RefreshCcw,
  ArrowUp, ArrowDown, ArrowUpDown, History, MessageSquare, Send, StickyNote, Pencil, Paperclip,
  Pause, Play, Ban
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BranchJobs = ({ branchIdOverride, readOnly = false }) => {
  const [jobs, setJobs] = useState([]);
  const [archivedJobs, setArchivedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [branchUsers, setBranchUsers] = useState([]);
  const [trackingUsers, setTrackingUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('active');
  const [activeSubTab, setActiveSubTab] = useState('active'); // 'active', 'paused', 'completed', 'cancelled', 'forwarded'
  const [forwardedToFilter, setForwardedToFilter] = useState('all'); // İletilen yer filtresi
  
  // Mevcut kullanıcı bilgisi
  const currentUser = JSON.parse(localStorage.getItem('fortis_user') || '{}');
  
  // Arşiv açıklaması modal state'leri
  const [archiveReasonModalOpen, setArchiveReasonModalOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveTarget, setArchiveTarget] = useState({ type: null, id: null });
  const [archivingItem, setArchivingItem] = useState(false);
  
  // Sıralama state'i
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  
  // İlerleme modal state'i
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  
  // Durum güncelleme modal state'i
  const [statusUpdateModalOpen, setStatusUpdateModalOpen] = useState(false);
  const [statusUpdateJob, setStatusUpdateJob] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusDescription, setStatusDescription] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // İş iletme modal state'leri
  const [forwardModalOpen, setForwardModalOpen] = useState(false);
  const [forwardingJob, setForwardingJob] = useState(null);
  const [forwardTargetPanel, setForwardTargetPanel] = useState('purchasing');
  const [forwardNote, setForwardNote] = useState('');
  const [forwardPriority, setForwardPriority] = useState('normal');
  const [isForwarding, setIsForwarding] = useState(false);
  
  // Ara verme modal state'leri
  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [jobToPause, setJobToPause] = useState(null);
  const [pauseLoading, setPauseLoading] = useState(false);
  
  // İşe bağlı öğeler için state'ler
  const [reportsModalOpen, setReportsModalOpen] = useState(false);
  const [schedulesModalOpen, setSchedulesModalOpen] = useState(false);
  const [tasksModalOpen, setTasksModalOpen] = useState(false);
  const [jobReports, setJobReports] = useState([]);
  const [jobSchedules, setJobSchedules] = useState([]);
  const [jobTasks, setJobTasks] = useState([]);
  const [itemLoading, setItemLoading] = useState(false);
  
  // Arşiv state'leri
  const [archivedSchedules, setArchivedSchedules] = useState([]);
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [schedulesTab, setSchedulesTab] = useState('active'); // 'active' veya 'archive'
  const [tasksTab, setTasksTab] = useState('active'); // 'active' veya 'archive'
  
  // Ekleme form state'leri
  const [addReportOpen, setAddReportOpen] = useState(false);
  const [addScheduleOpen, setAddScheduleOpen] = useState(false);
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [newReport, setNewReport] = useState({ title: '', content: '', report_type: 'general' });
  const [newSchedule, setNewSchedule] = useState({ title: '', description: '', scheduled_date: '', scheduled_time: '' });
  const [newTask, setNewTask] = useState({ title: '', description: '', assigned_to: '', due_date: '', priority: 'normal' });
  
  // Belge yükleme state'leri
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  
  // İş belgesi yükleme state'leri (form için)
  const [jobDocuments, setJobDocuments] = useState([]);
  const [pendingJobDocuments, setPendingJobDocuments] = useState([]);
  const [jobDocumentsLoading, setJobDocumentsLoading] = useState(false);
  
  // Detay modal state'leri
  const [reportDetailModal, setReportDetailModal] = useState(false);
  const [scheduleDetailModal, setScheduleDetailModal] = useState(false);
  const [taskDetailModal, setTaskDetailModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [itemDocuments, setItemDocuments] = useState([]);
  
  // Görev güncelleme state'leri
  const [taskUpdateModal, setTaskUpdateModal] = useState(false);
  const [taskUpdateText, setTaskUpdateText] = useState('');
  const [taskUpdateFiles, setTaskUpdateFiles] = useState([]);
  const [submittingTaskUpdate, setSubmittingTaskUpdate] = useState(false);
  const [completingTask, setCompletingTask] = useState(false);
  
  // İş Programı güncelleme state'leri
  const [scheduleUpdateModal, setScheduleUpdateModal] = useState(false);
  const [scheduleUpdateText, setScheduleUpdateText] = useState('');
  const [scheduleUpdateFiles, setScheduleUpdateFiles] = useState([]);
  const [submittingScheduleUpdate, setSubmittingScheduleUpdate] = useState(false);
  const [completingSchedule, setCompletingSchedule] = useState(false);
  
  // Talepler state'leri
  const [managerRequests, setManagerRequests] = useState([]);
  const [branchIncomingRequests, setBranchIncomingRequests] = useState([]); // Şubeye gelen tüm talepler
  const [talepModalOpen, setTalepModalOpen] = useState(false);
  const [talepSubmitting, setTalepSubmitting] = useState(false);
  const [selectedTalep, setSelectedTalep] = useState(null);
  const [taleplerSubTab, setTaleplerSubTab] = useState('gelen'); // Varsayılan olarak gelen talepler
  const [talepSortConfig, setTalepSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [talepActionLoading, setTalepActionLoading] = useState(false);
  const [talepActionNote, setTalepActionNote] = useState('');
  const [talepActionType, setTalepActionType] = useState(null); // 'approve', 'approve_forward', 'reject'
  const [showRecipientModal, setShowRecipientModal] = useState(false); // Genel talepler için kişi seçim modalı
  const [selectedRecipientId, setSelectedRecipientId] = useState(''); // Seçilen alıcı kişi
  const [allUsers, setAllUsers] = useState([]); // Tüm kullanıcılar listesi
  const [showRejectModal, setShowRejectModal] = useState(false); // Red nedeni modalı
  const [rejectReason, setRejectReason] = useState(''); // Red nedeni
  
  // İş Duvarı state'leri
  const [wallModalOpen, setWallModalOpen] = useState(false);
  const [wallPosts, setWallPosts] = useState([]);
  const [wallLoading, setWallLoading] = useState(false);
  const [wallPostContent, setWallPostContent] = useState('');
  const [wallPostDocuments, setWallPostDocuments] = useState([]);
  const [wallSubmitting, setWallSubmitting] = useState(false);
  const [selectedWallPost, setSelectedWallPost] = useState(null);
  const [wallDetailModalOpen, setWallDetailModalOpen] = useState(false);
  const [wallCommentContent, setWallCommentContent] = useState('');
  const [wallCommentDocuments, setWallCommentDocuments] = useState([]);
  const [editingWallPost, setEditingWallPost] = useState(null);
  const [editWallPostModalOpen, setEditWallPostModalOpen] = useState(false);
  const [editingWallComment, setEditingWallComment] = useState(null);
  const [editWallCommentModalOpen, setEditWallCommentModalOpen] = useState(false);
  
  // İş İptal Modal state'leri
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingJob, setCancellingJob] = useState(false);
  // İşi Tamamla modal
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [completeNote, setCompleteNote] = useState('');
  const [completingJob, setCompletingJob] = useState(false);
  
  const [talepFormData, setTalepFormData] = useState({
    category: 'genel_talepler',
    title: '',
    description: '',
    priority: 'normal',
    documents: [],
    expected_delivery_date: ''
  });
  
  // Talep kategorileri
  const talepCategories = [
    { id: 'ik', label: 'İK Talebi' },
    { id: 'satin_alma', label: 'Satın Alma Talebi' },
    { id: 'muhasebe', label: 'Muhasebe Talebi' },
    { id: 'idari_isler', label: 'İdari İşler Talebi' },
    { id: 'genel_talepler', label: 'Genel Talep' }
  ];
  
  // Kategorilere göre talep başlıkları
  const talepTitles = {
    ik: ['İzin Talebi', 'Avans Talebi', 'Maaş Bilgi Talebi', 'Belge Talebi', 'Diğer İK Talebi'],
    satin_alma: ['Malzeme Talebi', 'Ekipman Talebi', 'Ofis Malzemesi Talebi', 'Diğer Satın Alma Talebi'],
    muhasebe: ['Fatura Bilgi Talebi', 'Ödeme Talebi', 'Masraf Talebi', 'Diğer Muhasebe Talebi'],
    idari_isler: ['Bakım/Onarım Talebi', 'Temizlik Talebi', 'Güvenlik Talebi', 'Diğer İdari Talep'],
    genel_talepler: ['Depo Ürün İsteği', 'Genel Bilgi Talebi', 'Diğer Talep']
  };
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    tracking_users: [],
    company: '',
    start_date: new Date().toISOString().split('T')[0],
    estimated_end_date: '',
    locations: [''],
    description: '',
    status: 'active'
  });
  
  // Taşeronlar listesi - İşi Yapan Şirket seçimi için
  const [subcontractors, setSubcontractors] = useState([]);
  const [companySelectValue, setCompanySelectValue] = useState(''); // 'fortis', taşeron id'si, veya 'diger'
  const [customCompanyName, setCustomCompanyName] = useState(''); // "Diğer" seçildiğinde elle yazılan isim

  useEffect(() => {
    if (activeTab === 'archive') {
      loadArchivedJobs();
    } else if (activeTab === 'talepler') {
      loadManagerRequests();
      loadBranchIncomingRequests(); // Şubeye gelen talepleri de yükle
    } else {
      loadJobs();
    }
    loadBranchUsers();
    loadTrackingUsers();
    loadSubcontractors();
    // Badge sayısı için her zaman talepleri yükle
    loadBranchIncomingRequests();
  }, [activeTab]);
  
  // Taşeronları yükle
  const loadSubcontractors = async () => {
    try {
      const res = await axios.get(`${API}/subcontractors`, { headers: getAuthHeaders() });
      setSubcontractors(res.data || []);
    } catch (error) {
      console.error('Taşeronlar yüklenirken hata:', error);
    }
  };
  
  // Şirket seçimi değiştiğinde formData'yı güncelle
  const handleCompanySelect = (value) => {
    setCompanySelectValue(value);
    if (value === 'fortis') {
      setFormData(prev => ({...prev, company: 'Fortis'}));
      setCustomCompanyName('');
    } else if (value === 'diger') {
      setFormData(prev => ({...prev, company: customCompanyName}));
    } else {
      // Taşeron seçildi - taşeron adını bul
      const selectedSub = subcontractors.find(s => s.id === value);
      if (selectedSub) {
        setFormData(prev => ({...prev, company: selectedSub.name}));
      }
      setCustomCompanyName('');
    }
  };
  
  // "Diğer" seçildiğinde elle yazılan şirket adını güncelle
  const handleCustomCompanyChange = (value) => {
    setCustomCompanyName(value);
    setFormData(prev => ({...prev, company: value}));
  };

  const loadJobs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/branch/jobs`, { headers: getAuthHeaders() });
      setJobs(res.data || []);
    } catch (error) {
      console.error('İşler yüklenirken hata:', error);
      toast.error('İşler yüklenemedi');
    }
    setLoading(false);
  };

  const loadArchivedJobs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/branch/jobs/archived`, { headers: getAuthHeaders() });
      setArchivedJobs(res.data || []);
    } catch (error) {
      console.error('Arşivlenmiş işler yüklenirken hata:', error);
      toast.error('Arşivlenmiş işler yüklenemedi');
    }
    setLoading(false);
  };

  // Talepler fonksiyonları
  const loadManagerRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/work/requests/my`, { headers: getAuthHeaders() });
      setManagerRequests(res.data || []);
    } catch (error) {
      console.error('Talepler yüklenirken hata:', error);
    }
    setLoading(false);
  };

  // Şubeye gelen TÜM talepleri yükle (çalışanların talepleri) - seen bilgisi ile
  const loadBranchIncomingRequests = async () => {
    try {
      const res = await axios.get(`${API}/work/requests/incoming-with-seen`, { headers: getAuthHeaders() });
      setBranchIncomingRequests(res.data || []);
    } catch (error) {
      console.error('Şubeye gelen talepler yüklenirken hata:', error);
    }
  };

  // Talebi görüntülendi olarak işaretle
  const markRequestAsSeen = async (requestId) => {
    try {
      await axios.post(`${API}/work/requests/${requestId}/mark-seen-by-manager`, {}, { headers: getAuthHeaders() });
      // State'i güncelle
      setBranchIncomingRequests(prev => 
        prev.map(r => r.id === requestId ? { ...r, is_seen: true } : r)
      );
    } catch (error) {
      console.error('Talep görüntülendi işaretlenemedi:', error);
    }
  };

  // İşi görüntülendi olarak işaretle
  const markJobAsSeen = async (jobId) => {
    try {
      await axios.post(`${API}/branch/jobs/${jobId}/mark-seen`, {}, { headers: getAuthHeaders() });
      // State'i güncelle
      setJobs(prev => 
        prev.map(j => j.id === jobId ? { ...j, is_seen: true } : j)
      );
    } catch (error) {
      console.error('İş görüntülendi işaretlenemedi:', error);
    }
  };

  const handleCreateTalep = async () => {
    if (talepFormData.documents.length === 0) {
      toast.error('En az bir belge yüklemek zorunludur');
      return;
    }
    
    if (!talepFormData.title) {
      toast.error('Talep başlığı zorunludur');
      return;
    }
    
    setTalepSubmitting(true);
    try {
      const res = await axios.post(`${API}/work/requests`, {
        ...talepFormData
      }, { headers: getAuthHeaders() });
      
      if (res.status === 200 || res.status === 201) {
        toast.success('Talep oluşturuldu');
        setTalepModalOpen(false);
        setTalepFormData({
          category: 'genel_talepler',
          title: '',
          description: '',
          priority: 'normal',
          documents: [],
          expected_delivery_date: ''
        });
        loadManagerRequests();
      }
    } catch (error) {
      console.error('Talep oluşturma hatası:', error);
      toast.error(error.response?.data?.detail || 'Talep oluşturulamadı');
    }
    setTalepSubmitting(false);
  };

  const handleTalepFileUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setTalepFormData(prev => {
          const newDocs = [...prev.documents, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: event.target.result
          }];
          
          const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
          const shouldSetTitle = prev.documents.length === 0 && index === 0 && !prev.title;
          
          return {
            ...prev,
            documents: newDocs,
            title: shouldSetTitle ? fileNameWithoutExt : prev.title
          };
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const removeTalepFile = (index) => {
    setTalepFormData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  // Talep işlemleri (Onayla, Onayla ve İlet, Reddet)
  const handleTalepAction = async (actionType, recipientId = null) => {
    if (!selectedTalep) return;
    
    // Genel kategori için "Onayla ve İlet" seçildiğinde kişi seçim modalını aç
    if (actionType === 'approve_forward' && 
        (selectedTalep.category === 'genel_talepler' || selectedTalep.category === 'genel') && 
        !recipientId) {
      loadAllUsers(); // Kullanıcı listesini yükle
      setShowRecipientModal(true);
      return;
    }
    
    // Reddet için modal aç
    if (actionType === 'reject') {
      setShowRejectModal(true);
      return;
    }
    
    setTalepActionLoading(true);
    try {
      let newStatus = '';
      let targetDepartment = null;
      let targetUserId = recipientId;
      
      if (actionType === 'approve') {
        // Şube yöneticisi onayladığında talep tamamlanmış sayılır (Bitirilen Talepler'e düşer)
        newStatus = 'completed';
      } else if (actionType === 'approve_forward') {
        // Kategoriye göre yönlendir
        const categoryToDept = {
          'ik': 'hr',
          'satin_alma': 'purchasing',
          'muhasebe': 'accounting',
          'idari_isler': 'hr',
          'genel_talepler': recipientId ? 'custom' : 'hr'
        };
        targetDepartment = categoryToDept[selectedTalep.category] || 'hr';
        newStatus = recipientId ? 'forwarded_to_user' : `forwarded_to_${targetDepartment}`;
      }
      
      await axios.put(`${API}/work/requests/${selectedTalep.id}`, {
        status: newStatus,
        response_message: talepActionNote,
        target_department: targetDepartment,
        target_user_id: targetUserId
      }, { headers: getAuthHeaders() });
      
      const actionMessages = {
        'approve': 'Talep onaylandı ve tamamlandı',
        'approve_forward': recipientId ? 'Talep onaylandı ve seçilen kişiye iletildi' : 'Talep onaylandı ve ilgili birime iletildi'
      };
      
      toast.success(actionMessages[actionType]);
      setSelectedTalep(null);
      setTalepActionNote('');
      setTalepActionType(null);
      setShowRecipientModal(false);
      setSelectedRecipientId('');
      loadBranchIncomingRequests();
      loadManagerRequests();
    } catch (error) {
      console.error('Talep işlemi hatası:', error);
      toast.error('Talep işlemi başarısız');
    }
    setTalepActionLoading(false);
  };

  // Talep reddetme işlemi
  const handleRejectTalep = async () => {
    if (!selectedTalep || !rejectReason.trim()) {
      toast.error('Red nedeni zorunludur');
      return;
    }
    
    setTalepActionLoading(true);
    try {
      await axios.put(`${API}/work/requests/${selectedTalep.id}`, {
        status: 'rejected_by_branch',
        response_message: rejectReason,
        rejection_reason: rejectReason
      }, { headers: getAuthHeaders() });
      
      toast.success('Talep reddedildi');
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedTalep(null);
      setTalepActionNote('');
      loadBranchIncomingRequests();
      loadManagerRequests();
    } catch (error) {
      console.error('Talep red hatası:', error);
      toast.error('Talep red işlemi başarısız');
    }
    setTalepActionLoading(false);
  };

  // Talepler için sıralama fonksiyonları
  const handleTalepSort = (key) => {
    let direction = 'asc';
    if (talepSortConfig.key === key && talepSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setTalepSortConfig({ key, direction });
  };

  const getTalepSortIcon = (key) => {
    if (talepSortConfig.key !== key) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return talepSortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-purple-600" />
      : <ArrowDown className="h-4 w-4 text-purple-600" />;
  };

  const sortTalepler = (data) => {
    if (!talepSortConfig.key || !data) return data;
    
    return [...data].sort((a, b) => {
      let aVal, bVal;
      
      switch (talepSortConfig.key) {
        case 'request_number':
          aVal = a.request_number || '';
          bVal = b.request_number || '';
          break;
        case 'created_at':
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
          break;
        case 'category':
          aVal = a.category || '';
          bVal = b.category || '';
          break;
        case 'title':
          aVal = a.title || '';
          bVal = b.title || '';
          break;
        case 'priority':
          const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
          aVal = priorityOrder[a.priority] || 0;
          bVal = priorityOrder[b.priority] || 0;
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'owner_name':
          aVal = a.owner_name || '';
          bVal = b.owner_name || '';
          break;
        default:
          aVal = a[talepSortConfig.key] || '';
          bVal = b[talepSortConfig.key] || '';
      }
      
      if (aVal < bVal) return talepSortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return talepSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getFilteredTalepler = () => {
    let filtered;
    if (taleplerSubTab === 'gelen') {
      // Şubeye gelen bekleyen talepler
      filtered = branchIncomingRequests.filter(r => r.status === 'pending');
    } else if (taleplerSubTab === 'onayladigim') {
      // Şubeye gelen ve ilgili birimlere iletilen talepler (sadece forwarded_to_* durumları)
      filtered = branchIncomingRequests.filter(r => 
        r.status?.startsWith('forwarded_to_')
      );
    } else if (taleplerSubTab === 'bitirilen') {
      // Şubeye gelen ve bitirilen talepler (completed + converted_to_job + approved)
      filtered = branchIncomingRequests.filter(r => ['completed', 'converted_to_job', 'approved'].includes(r.status));
    } else if (taleplerSubTab === 'aktif') {
      // Şube yöneticisinin kendi aktif talepleri
      filtered = managerRequests.filter(r => !['approved', 'completed', 'converted_to_job', 'rejected', 'rejected_by_branch', 'rejected_by_hr'].includes(r.status));
    } else if (taleplerSubTab === 'onaylanan') {
      filtered = managerRequests.filter(r => r.status === 'approved');
    } else if (taleplerSubTab === 'benim_bitirilen') {
      // Şube yöneticisinin kendi bitirilen talepleri
      filtered = managerRequests.filter(r => ['completed', 'converted_to_job'].includes(r.status));
    } else if (taleplerSubTab === 'reddedilen') {
      filtered = managerRequests.filter(r => ['rejected', 'rejected_by_branch', 'rejected_by_hr'].includes(r.status));
    } else if (taleplerSubTab === 'reddettigim') {
      // Şube yöneticisinin reddettiği gelen talepler
      filtered = branchIncomingRequests.filter(r => ['rejected', 'rejected_by_branch'].includes(r.status));
    } else {
      filtered = managerRequests;
    }
    return sortTalepler(filtered);
  };

  const loadBranchUsers = async () => {
    try {
      const res = await axios.get(`${API}/branch/users-for-selection`, { headers: getAuthHeaders() });
      setBranchUsers(res.data || []);
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
    }
  };

  // İş takip listesi: kendi şube kullanıcıları + merkezi panel (İK/Muhasebe/Satın Alma/Yönetim Kurulu/Depo) kullanıcıları
  const loadTrackingUsers = async () => {
    try {
      const res = await axios.get(`${API}/branch/tracking-users-for-selection`, { headers: getAuthHeaders() });
      setTrackingUsers(res.data || []);
    } catch (error) {
      console.error('Takip kullanıcıları yüklenirken hata:', error);
    }
  };

  // Genel talepler için TÜM kullanıcıları yükle (tüm paneller, tüm şubeler)
  const loadAllUsers = async () => {
    try {
      const res = await axios.get(`${API}/branch/all-users-for-forward`, { headers: getAuthHeaders() });
      // Tüm kullanıcıları al - filtre yok, serbest seçim
      setAllUsers(res.data || []);
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
      // Fallback: şube kullanıcılarını kullan
      setAllUsers(branchUsers);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      tracking_users: [],
      company: '',
      start_date: new Date().toISOString().split('T')[0],
      estimated_end_date: '',
      locations: [''],
      description: '',
      status: 'active'
    });
    setPendingJobDocuments([]);
    setCompanySelectValue('');
    setCustomCompanyName('');
  };

  // İş belgelerini yükle
  const loadJobDocuments = async (jobId) => {
    setJobDocumentsLoading(true);
    try {
      const res = await axios.get(`${API}/branch/jobs/${jobId}/documents`, { headers: getAuthHeaders() });
      setJobDocuments(res.data || []);
    } catch (error) {
      console.error('İş belgeleri yüklenirken hata:', error);
      if (error.response?.status === 401) {
        toast.error('Oturum süresi dolmuş olabilir. Sayfayı yenileyin.');
      } else {
        setJobDocuments([]);
      }
    }
    setJobDocumentsLoading(false);
  };

  // Belge seçimi (form için)
  const handleJobDocumentSelect = (e) => {
    const files = Array.from(e.target.files);
    setPendingJobDocuments(prev => [...prev, ...files]);
  };

  // Bekleyen belgeyi kaldır
  const removePendingDocument = (index) => {
    setPendingJobDocuments(prev => prev.filter((_, i) => i !== index));
  };

  // Belgeleri yükle (iş oluşturulduktan sonra)
  const uploadJobDocuments = async (jobId) => {
    if (pendingJobDocuments.length === 0) return { success: 0, failed: 0 };
    
    let successCount = 0;
    let failedCount = 0;
    
    for (const file of pendingJobDocuments) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('item_type', 'job');
        formDataUpload.append('item_id', jobId);
        formDataUpload.append('job_id', jobId);
        
        await axios.post(`${API}/upload/document`, formDataUpload, {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        });
        successCount++;
      } catch (error) {
        console.error('Belge yüklenirken hata:', error);
        const errorMsg = error.response?.data?.detail || 'Bilinmeyen hata';
        toast.error(`${file.name} yüklenemedi: ${errorMsg}`);
        failedCount++;
      }
    }
    
    return { success: successCount, failed: failedCount };
  };

  // Belge indirme
  const downloadJobDocument = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.id}/download`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename || doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Belge indirme hatası:', error);
      toast.error('Belge indirilemedi');
    }
  };

  const handleCreateJob = async () => {
    if (!formData.name.trim()) {
      toast.error('İş adı gerekli');
      return;
    }
    if (!formData.company.trim()) {
      toast.error('Şirket adı gerekli');
      return;
    }
    if (!formData.start_date) {
      toast.error('Başlangıç tarihi gerekli');
      return;
    }
    if (!formData.estimated_end_date) {
      toast.error('Tahmini bitiş tarihi gerekli');
      return;
    }

    try {
      const payload = {
        ...formData,
        locations: formData.locations.filter(loc => loc.trim() !== '')
      };
      const response = await axios.post(`${API}/branch/jobs`, payload, { headers: getAuthHeaders() });
      const jobId = response.data.job_id;
      
      // Bekleyen belgeleri yükle
      if (pendingJobDocuments.length > 0 && jobId) {
        const uploadResult = await uploadJobDocuments(jobId);
        if (uploadResult.failed === 0) {
          toast.success(`İş oluşturuldu ve ${uploadResult.success} belge yüklendi`);
        } else if (uploadResult.success > 0) {
          toast.warning(`İş oluşturuldu. ${uploadResult.success} belge yüklendi, ${uploadResult.failed} belge yüklenemedi`);
        } else {
          toast.warning(`İş oluşturuldu ancak belgeler yüklenemedi`);
        }
      } else {
        toast.success('İş oluşturuldu');
      }
      
      setCreateModalOpen(false);
      resetForm();
      loadJobs();
    } catch (error) {
      console.error('İş oluşturulurken hata:', error);
      toast.error('İş oluşturulamadı');
    }
  };

  const handleEditJob = async () => {
    if (!formData.name.trim()) {
      toast.error('İş adı gerekli');
      return;
    }

    try {
      const payload = {
        ...formData,
        locations: formData.locations.filter(loc => loc.trim() !== '')
      };
      await axios.put(`${API}/branch/jobs/${selectedJob.id}`, payload, { headers: getAuthHeaders() });
      toast.success('İş güncellendi');
      setEditModalOpen(false);
      resetForm();
      loadJobs();
    } catch (error) {
      console.error('İş güncellenirken hata:', error);
      toast.error('İş güncellenemedi');
    }
  };

  // Arşiv açıklaması modalı için fonksiyonlar
  const openArchiveReasonModal = (type, id) => {
    setArchiveTarget({ type, id });
    setArchiveReason('');
    setArchiveReasonModalOpen(true);
  };

  const handleConfirmArchive = async () => {
    if (!archiveReason.trim()) {
      toast.error('Arşiv açıklaması zorunludur');
      return;
    }
    
    setArchivingItem(true);
    try {
      const { type, id } = archiveTarget;
      
      if (type === 'job') {
        await axios.put(`${API}/branch/jobs/${id}/archive`, { archive_reason: archiveReason }, { headers: getAuthHeaders() });
        toast.success('İş arşive gönderildi');
        setArchiveModalOpen(false);
        setSelectedJob(null);
        loadJobs();
        loadArchivedJobs();
      } else if (type === 'schedule') {
        await axios.delete(`${API}/branch/jobs/${selectedJob.id}/schedules/${id}`, { 
          headers: getAuthHeaders(),
          data: { archive_reason: archiveReason }
        });
        toast.success('İş programı arşivlendi');
        openSchedulesModal(selectedJob);
      } else if (type === 'task') {
        await axios.delete(`${API}/branch/jobs/${selectedJob.id}/tasks/${id}`, {
          headers: getAuthHeaders(),
          data: { archive_reason: archiveReason }
        });
        toast.success('Görev arşivlendi');
        openTasksModal(selectedJob);
      }
      
      setArchiveReasonModalOpen(false);
      setArchiveReason('');
      setArchiveTarget({ type: null, id: null });
    } catch (error) {
      console.error('Arşivleme hatası:', error);
      toast.error(error.response?.data?.detail || 'Arşivleme başarısız');
    } finally {
      setArchivingItem(false);
    }
  };

  const handleArchiveJob = async () => {
    openArchiveReasonModal('job', selectedJob.id);
  };

  const handleRestoreJob = async (jobId) => {
    try {
      await axios.put(`${API}/branch/jobs/${jobId}/restore`, {}, { headers: getAuthHeaders() });
      toast.success('İş arşivden geri yüklendi');
      // Her iki listeyi de güncelle
      loadArchivedJobs();
      loadJobs();
    } catch (error) {
      console.error('İş geri yüklenirken hata:', error);
      toast.error('İş geri yüklenemedi');
    }
  };

  const handleUpdateProgress = async () => {
    if (!selectedJob) return;
    
    try {
      await axios.put(`${API}/branch/jobs/${selectedJob.id}/progress?progress=${progressValue}`, {}, { headers: getAuthHeaders() });
      toast.success('İlerleme güncellendi');
      setProgressModalOpen(false);
      setDetailModalOpen(false);
      loadJobs();
    } catch (error) {
      console.error('İlerleme güncellenirken hata:', error);
      toast.error('İlerleme güncellenemedi');
    }
  };

  const openEditModal = (job) => {
    setSelectedJob(job);
    setFormData({
      name: job.name || '',
      tracking_users: job.tracking_users || [],
      company: job.company || '',
      start_date: job.start_date || '',
      estimated_end_date: job.estimated_end_date || '',
      locations: job.locations?.length > 0 ? job.locations : [''],
      description: job.description || '',
      status: job.status || 'active'
    });
    setEditModalOpen(true);
  };

  const openDetailModal = (job) => {
    setSelectedJob(job);
    setNewStatus(job.status || 'active');
    setStatusDescription('');
    setDetailModalOpen(true);
    loadJobDocuments(job.id);
  };

  const openArchiveModal = (job) => {
    setSelectedJob(job);
    setArchiveModalOpen(true);
  };

  const openProgressModal = (job) => {
    setSelectedJob(job);
    setProgressValue(job.progress || 0);
    setProgressModalOpen(true);
  };

  // Durum güncelleme modalını aç
  const openStatusUpdateModal = (job) => {
    setStatusUpdateJob(job);
    setNewStatus(job.status || 'active');
    setStatusDescription('');
    setStatusUpdateModalOpen(true);
  };

  // Durumu güncelle
  const handleStatusUpdate = async (jobToUpdate = null) => {
    const targetJob = jobToUpdate || statusUpdateJob;
    if (!targetJob || !newStatus) return;
    
    setUpdatingStatus(true);
    try {
      const response = await axios.put(
        `${API}/branch/jobs/${targetJob.id}/status`,
        { 
          status: newStatus,
          status_description: statusDescription 
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data) {
        // Listeyi güncelle
        setJobs(prev => prev.map(j => 
          j.id === targetJob.id 
            ? { ...j, status: newStatus, status_description: statusDescription, updated_at: new Date().toISOString() }
            : j
        ));
        // selectedJob'u da güncelle (modal açıksa)
        if (selectedJob && selectedJob.id === targetJob.id) {
          setSelectedJob(prev => ({ ...prev, status: newStatus, status_description: statusDescription }));
        }
        toast.success('Durum başarıyla güncellendi');
        setStatusUpdateModalOpen(false);
        
        // Yeni duruma göre ilgili tab'a geç
        const statusToTab = {
          'active': 'active',
          'in_progress': 'active',  // Devam Ediyor -> Aktif İşler tabına
          'paused': 'paused',
          'on_hold': 'paused',
          'completed': 'completed',
          'cancelled': 'cancelled'
        };
        if (statusToTab[newStatus]) {
          setActiveSubTab(statusToTab[newStatus]);
        }
        
        // Reset states
        setNewStatus('');
        setStatusDescription('');
      }
    } catch (error) {
      console.error('Durum güncelleme hatası:', error);
      toast.error(error.response?.data?.detail || 'Durum güncellenirken hata oluştu');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Hızlı durum güncelleme (Ara Ver / Aktif Et / İptal Et butonları için)
  const handleQuickStatusUpdate = async (job, targetStatus) => {
    // Ara Ver için sebep modalı aç
    if (targetStatus === 'paused') {
      setJobToPause(job);
      setPauseReason('');
      setPauseModalOpen(true);
      return;
    }
    
    const statusMessages = {
      'paused': 'İşe ara verildi',
      'active': 'İş tekrar aktif edildi',
      'cancelled': 'İş iptal edildi',
      'completed': 'İş tamamlandı'
    };
    
    const confirmMessages = {
      'paused': 'Bu işe ara vermek istediğinize emin misiniz?',
      'active': 'Bu işi tekrar aktif etmek istediğinize emin misiniz?',
      'cancelled': 'Bu işi iptal etmek istediğinize emin misiniz?',
      'completed': 'Bu işi tamamlamak istediğinize emin misiniz?'
    };
    
    if (!window.confirm(confirmMessages[targetStatus])) return;
    
    try {
      await axios.put(
        `${API}/branch/jobs/${job.id}/status`,
        { status: targetStatus },
        { headers: getAuthHeaders() }
      );
      
      toast.success(statusMessages[targetStatus]);
      loadJobs();
      setDetailModalOpen(false);
      
      // İlgili tab'a geç
      const statusToTab = {
        'active': 'active',
        'paused': 'paused',
        'completed': 'completed',
        'cancelled': 'cancelled'
      };
      setActiveSubTab(statusToTab[targetStatus]);
    } catch (error) {
      console.error('Durum güncellenirken hata:', error);
      toast.error(error.response?.data?.detail || 'Durum güncellenemedi');
    }
  };

  // Ara verme işlemini tamamla (sebep ile)
  const handlePauseJob = async () => {
    if (!pauseReason.trim()) {
      toast.error('Lütfen ara verme sebebini girin');
      return;
    }
    
    setPauseLoading(true);
    try {
      await axios.put(`${API}/branch/jobs/${jobToPause.id}/status`, { 
        status: 'paused',
        pause_reason: pauseReason
      }, { headers: getAuthHeaders() });
      
      toast.success('İşe ara verildi');
      setPauseModalOpen(false);
      setPauseReason('');
      setJobToPause(null);
      loadJobs();
      setDetailModalOpen(false);
      setActiveSubTab('paused');
    } catch (error) {
      console.error('Ara verme hatası:', error);
      toast.error(error.response?.data?.detail || 'Ara verilemedi');
    }
    setPauseLoading(false);
  };

  // İptal modalını aç
  const openCancelModal = (job) => {
    setSelectedJob(job);
    setCancelReason('');
    setCancelModalOpen(true);
  };

  // İşi iptal et (açıklama ile)
  const handleCancelJob = async () => {
    if (!cancelReason.trim()) {
      toast.error('Lütfen iptal açıklaması girin');
      return;
    }
    
    setCancellingJob(true);
    try {
      await axios.put(`${API}/branch/jobs/${selectedJob.id}/status`, {
        status: 'cancelled',
        status_description: cancelReason.trim()
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('İş başarıyla iptal edildi');
      setCancelModalOpen(false);
      setCancelReason('');
      setDetailModalOpen(false);
      loadJobs();
      setActiveSubTab('cancelled');
    } catch (error) {
      console.error('İş iptal edilirken hata:', error);
      toast.error(error.response?.data?.detail || 'İş iptal edilemedi');
    } finally {
      setCancellingJob(false);
    }
  };

  // İşi Tamamla modalını aç
  const openCompleteModal = (job) => {
    setSelectedJob(job);
    setCompleteNote('');
    setCompleteModalOpen(true);
  };

  // İşi tamamla (açıklama ile) - takip edenler de tamamlanmış görür ve açıklamayı görür
  const handleCompleteJob = async () => {
    if (!completeNote.trim()) {
      toast.error('Lütfen tamamlama açıklaması girin');
      return;
    }
    setCompletingJob(true);
    try {
      await axios.put(`${API}/branch/jobs/${selectedJob.id}/status`, {
        status: 'completed',
        status_description: completeNote.trim()
      }, { headers: getAuthHeaders() });

      toast.success('İş tamamlandı');
      setCompleteModalOpen(false);
      setCompleteNote('');
      setDetailModalOpen(false);
      loadJobs();
      setActiveSubTab('completed');
    } catch (error) {
      console.error('İş tamamlanırken hata:', error);
      toast.error(error.response?.data?.detail || 'İş tamamlanamadı');
    } finally {
      setCompletingJob(false);
    }
  };

  const openForwardModal = (job) => {
    setForwardingJob(job);
    setForwardTargetPanel('purchasing');
    setForwardNote('');
    setForwardPriority('normal');
    setForwardModalOpen(true);
  };

  // İşi ilet
  const handleForwardJob = async () => {
    if (!forwardingJob) return;
    
    setIsForwarding(true);
    try {
      const response = await axios.post(
        `${API}/branch/jobs/${forwardingJob.id}/forward`,
        {
          target_panel: forwardTargetPanel,
          note: forwardNote,
          priority: forwardPriority
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data) {
        // Listeyi güncelle - iletildi bilgisini ekle
        setJobs(prev => prev.map(j => 
          j.id === forwardingJob.id 
            ? { 
                ...j, 
                forwarded_to: forwardTargetPanel,
                forwarded_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              }
            : j
        ));
        
        const panelNames = {
          'purchasing': 'Satın Alma',
          'hr': 'İK',
          'admin': 'İdari İşler',
          'accounting': 'Muhasebe'
        };
        
        toast.success(`İş ${panelNames[forwardTargetPanel]} paneline iletildi${response.data.seri_no ? ` (${response.data.seri_no})` : ''}`);
        setForwardModalOpen(false);
        
        // Reset states
        setForwardingJob(null);
        setForwardNote('');
        setForwardPriority('normal');
      }
    } catch (error) {
      console.error('İş iletme hatası:', error);
      toast.error(error.response?.data?.detail || 'İş iletilirken hata oluştu');
    } finally {
      setIsForwarding(false);
    }
  };

  const handleUserToggle = (userId) => {
    setFormData(prev => {
      const users = prev.tracking_users || [];
      if (users.includes(userId)) {
        return { ...prev, tracking_users: users.filter(id => id !== userId) };
      } else {
        return { ...prev, tracking_users: [...users, userId] };
      }
    });
  };

  const addLocation = () => {
    setFormData(prev => ({
      ...prev,
      locations: [...prev.locations, '']
    }));
  };

  const removeLocation = (index) => {
    if (formData.locations.length > 1) {
      setFormData(prev => ({
        ...prev,
        locations: prev.locations.filter((_, i) => i !== index)
      }));
    }
  };

  const updateLocation = (index, value) => {
    setFormData(prev => ({
      ...prev,
      locations: prev.locations.map((loc, i) => i === index ? value : loc)
    }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-gray-100 text-gray-700 border-gray-200"><Clock className="h-3 w-3 mr-1" />Aktif</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200"><Clock className="h-3 w-3 mr-1" />Devam Ediyor</Badge>;
      case 'paused':
      case 'on_hold':
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200"><AlertCircle className="h-3 w-3 mr-1" />Ara Verildi</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-700 border-green-200"><CheckCircle className="h-3 w-3 mr-1" />Tamamlandı</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700 border-red-200"><XCircle className="h-3 w-3 mr-1" />İptal Edildi</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700 border-gray-200"><Clock className="h-3 w-3 mr-1" />Aktif</Badge>;
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-green-500';
    if (progress >= 50) return 'bg-amber-500';
    if (progress >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  // Sıralama fonksiyonları
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-amber-600" />
      : <ArrowDown className="h-4 w-4 text-amber-600" />;
  };

  const sortedJobs = useMemo(() => {
    const jobsToSort = activeTab === 'archive' ? archivedJobs : jobs;
    if (!sortConfig.key) return jobsToSort;
    
    return [...jobsToSort].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'name':
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
          break;
        case 'company':
          aVal = a.company?.toLowerCase() || '';
          bVal = b.company?.toLowerCase() || '';
          break;
        case 'start_date':
          aVal = new Date(a.start_date || 0).getTime();
          bVal = new Date(b.start_date || 0).getTime();
          break;
        case 'estimated_end_date':
          aVal = new Date(a.estimated_end_date || 0).getTime();
          bVal = new Date(b.estimated_end_date || 0).getTime();
          break;
        case 'progress':
          aVal = a.progress || 0;
          bVal = b.progress || 0;
          break;
        case 'status':
          const statusOrder = { active: 1, completed: 2, cancelled: 3 };
          aVal = statusOrder[a.status] || 0;
          bVal = statusOrder[b.status] || 0;
          break;
        case 'created_at':
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
          break;
        case 'archived_at':
          aVal = new Date(a.archived_at || 0).getTime();
          bVal = new Date(b.archived_at || 0).getTime();
          break;
        case 'updated_at':
          aVal = new Date(a.updated_at || 0).getTime();
          bVal = new Date(b.updated_at || 0).getTime();
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [jobs, archivedJobs, sortConfig, activeTab]);

  // Alt tab'a göre filtreleme
  // Aktif İşler: aktif ve iletilmemiş işler
  // Ara Verilen: paused durumundaki işler
  // Tamamlanan: completed durumundaki işler
  // İptal Edilen: cancelled durumundaki işler
  // İletilenler: sadece iletilmiş işleri göster (forwarded_to olan)
  const filteredBySubTab = (() => {
    switch (activeSubTab) {
      case 'active':
        return sortedJobs.filter(job => !job.forwarded_to && job.status === 'active');
      case 'paused':
        return sortedJobs.filter(job => job.status === 'paused');
      case 'completed':
        return sortedJobs.filter(job => job.status === 'completed');
      case 'cancelled':
        return sortedJobs.filter(job => job.status === 'cancelled');
      case 'forwarded':
        return sortedJobs.filter(job => job.forwarded_to);
      default:
        return sortedJobs.filter(job => !job.forwarded_to);
    }
  })();

  // İletilen yer filtrelemesi (sadece forwarded tab'da aktif)
  const filteredByForwardedTo = activeSubTab === 'forwarded' && forwardedToFilter !== 'all'
    ? filteredBySubTab.filter(job => job.forwarded_to === forwardedToFilter)
    : filteredBySubTab;

  const filteredJobs = statusFilter === 'all' 
    ? filteredByForwardedTo 
    : filteredByForwardedTo.filter(job => job.status === statusFilter);

  // Tab sayaçları
  const activeJobsCount = jobs.filter(job => !job.forwarded_to && job.status === 'active').length;
  const pausedJobsCount = jobs.filter(job => job.status === 'paused').length;
  const completedJobsCount = jobs.filter(job => job.status === 'completed').length;
  const cancelledJobsCount = jobs.filter(job => job.status === 'cancelled').length;
  const forwardedJobsCount = jobs.filter(job => job.forwarded_to).length;
  
  // Okunmamış iş sayıları (her tab için)
  const unseenActiveCount = jobs.filter(job => !job.forwarded_to && job.status === 'active' && !job.is_seen).length;
  const unseenPausedCount = jobs.filter(job => job.status === 'paused' && !job.is_seen).length;
  const unseenCompletedCount = jobs.filter(job => job.status === 'completed' && !job.is_seen).length;
  const unseenCancelledCount = jobs.filter(job => job.status === 'cancelled' && !job.is_seen).length;
  const unseenForwardedCount = jobs.filter(job => job.forwarded_to && !job.is_seen).length;

  // İletilen yerlerin listesi (benzersiz)
  const forwardedToOptions = [
    { value: 'all', label: 'Tümü' },
    { value: 'purchasing', label: 'Satın Alma' },
    { value: 'hr', label: 'İK' },
    { value: 'accounting', label: 'Muhasebe' },
    { value: 'admin', label: 'İdari İşler' }
  ];

  // İş salt okunur modda mı? (Ara verilmiş veya İptal edilmiş işlerde ekleme/düzenleme/silme yapılamaz)
  const isJobReadOnly = selectedJob?.status === 'paused' || selectedJob?.status === 'cancelled';

  // İşe bağlı öğeler için fonksiyonlar
  const openReportsModal = async (job) => {
    setSelectedJob(job);
    setReportsModalOpen(true);
    setItemLoading(true);
    try {
      const res = await axios.get(`${API}/branch/jobs/${job.id}/reports`, { headers: getAuthHeaders() });
      setJobReports(res.data || []);
      // Görüntüleme kaydı
      await axios.post(`${API}/branch/jobs/${job.id}/mark-viewed?item_type=reports`, {}, { headers: getAuthHeaders() });
      // İş listesini güncelle
      loadJobs();
    } catch (error) {
      console.error('Raporlar yüklenirken hata:', error);
      toast.error('Raporlar yüklenemedi');
    }
    setItemLoading(false);
  };

  const openSchedulesModal = async (job) => {
    setSelectedJob(job);
    setSchedulesModalOpen(true);
    setSchedulesTab('active');
    setItemLoading(true);
    try {
      const [activeRes, archivedRes] = await Promise.all([
        axios.get(`${API}/branch/jobs/${job.id}/schedules`, { headers: getAuthHeaders() }),
        axios.get(`${API}/branch/jobs/${job.id}/schedules/archived`, { headers: getAuthHeaders() })
      ]);
      setJobSchedules(activeRes.data || []);
      setArchivedSchedules(archivedRes.data || []);
      // Görüntüleme kaydı
      await axios.post(`${API}/branch/jobs/${job.id}/mark-viewed?item_type=schedules`, {}, { headers: getAuthHeaders() });
      loadJobs();
    } catch (error) {
      console.error('İş programları yüklenirken hata:', error);
      toast.error('İş programları yüklenemedi');
    }
    setItemLoading(false);
  };

  const openTasksModal = async (job) => {
    setSelectedJob(job);
    setTasksModalOpen(true);
    setTasksTab('active');
    setItemLoading(true);
    try {
      const [activeRes, archivedRes] = await Promise.all([
        axios.get(`${API}/branch/jobs/${job.id}/tasks`, { headers: getAuthHeaders() }),
        axios.get(`${API}/branch/jobs/${job.id}/tasks/archived`, { headers: getAuthHeaders() })
      ]);
      setJobTasks(activeRes.data || []);
      setArchivedTasks(archivedRes.data || []);
      // Görüntüleme kaydı
      await axios.post(`${API}/branch/jobs/${job.id}/mark-viewed?item_type=tasks`, {}, { headers: getAuthHeaders() });
      loadJobs();
    } catch (error) {
      console.error('Görevler yüklenirken hata:', error);
      toast.error('Görevler yüklenemedi');
    }
    setItemLoading(false);
  };

  const handleAddReport = async () => {
    if (!newReport.title.trim()) {
      toast.error('Rapor başlığı gerekli');
      return;
    }
    try {
      const res = await axios.post(`${API}/branch/jobs/${selectedJob.id}/reports`, newReport, { headers: getAuthHeaders() });
      const reportId = res.data.report_id;
      
      if (selectedFiles.length > 0) {
        await uploadDocuments('report', reportId);
      }
      
      toast.success('Rapor eklendi');
      setAddReportOpen(false);
      setNewReport({ title: '', content: '', report_type: 'general' });
      setSelectedFiles([]);
      openReportsModal(selectedJob);
    } catch (error) {
      console.error('Rapor eklenirken hata:', error);
      toast.error('Rapor eklenemedi');
    }
  };

  const handleAddSchedule = async () => {
    if (!newSchedule.title.trim()) {
      toast.error('İş programı başlığı gerekli');
      return;
    }
    if (!newSchedule.scheduled_date) {
      toast.error('Tarih gerekli');
      return;
    }
    try {
      const res = await axios.post(`${API}/branch/jobs/${selectedJob.id}/schedules`, newSchedule, { headers: getAuthHeaders() });
      const scheduleId = res.data.schedule_id;
      
      if (selectedFiles.length > 0) {
        await uploadDocuments('schedule', scheduleId);
      }
      
      toast.success('İş programı eklendi');
      setAddScheduleOpen(false);
      setNewSchedule({ title: '', description: '', scheduled_date: '', scheduled_time: '' });
      setSelectedFiles([]);
      openSchedulesModal(selectedJob);
    } catch (error) {
      console.error('İş programı eklenirken hata:', error);
      toast.error('İş programı eklenemedi');
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title.trim()) {
      toast.error('Görev başlığı gerekli');
      return;
    }
    try {
      const res = await axios.post(`${API}/branch/jobs/${selectedJob.id}/tasks`, newTask, { headers: getAuthHeaders() });
      const taskId = res.data.task_id;
      
      if (selectedFiles.length > 0) {
        await uploadDocuments('task', taskId);
      }
      
      toast.success('Görev eklendi');
      setAddTaskOpen(false);
      setNewTask({ title: '', description: '', assigned_to: '', due_date: '', priority: 'normal' });
      setSelectedFiles([]);
      openTasksModal(selectedJob);
    } catch (error) {
      console.error('Görev eklenirken hata:', error);
      toast.error('Görev eklenemedi');
    }
  };

  const handleDeleteReport = async (reportId) => {
    try {
      await axios.delete(`${API}/branch/jobs/${selectedJob.id}/reports/${reportId}`, { headers: getAuthHeaders() });
      toast.success('Rapor silindi');
      openReportsModal(selectedJob);
    } catch (error) {
      toast.error('Rapor silinemedi');
    }
  };

  const handleArchiveSchedule = async (scheduleId) => {
    openArchiveReasonModal('schedule', scheduleId);
  };

  const handleRestoreSchedule = async (scheduleId) => {
    try {
      await axios.put(`${API}/branch/jobs/${selectedJob.id}/schedules/${scheduleId}/restore`, {}, { headers: getAuthHeaders() });
      toast.success('İş programı arşivden çıkarıldı');
      openSchedulesModal(selectedJob);
    } catch (error) {
      toast.error('İş programı arşivden çıkarılamadı');
    }
  };

  const handleArchiveTask = async (taskId) => {
    openArchiveReasonModal('task', taskId);
  };

  const handleRestoreTask = async (taskId) => {
    try {
      await axios.put(`${API}/branch/jobs/${selectedJob.id}/tasks/${taskId}/restore`, {}, { headers: getAuthHeaders() });
      toast.success('Görev arşivden çıkarıldı');
      openTasksModal(selectedJob);
    } catch (error) {
      toast.error('Görev arşivden çıkarılamadı');
    }
  };

  // Belge yükleme fonksiyonları
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadDocuments = async (itemType, itemId) => {
    if (selectedFiles.length === 0) return [];
    
    setUploadingFiles(true);
    const uploadedDocs = [];
    
    for (const file of selectedFiles) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('item_type', itemType);
        formDataUpload.append('item_id', itemId);
        formDataUpload.append('job_id', selectedJob.id);
        
        const res = await axios.post(`${API}/upload/document`, formDataUpload, {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        });
        uploadedDocs.push(res.data);
      } catch (error) {
        console.error('Belge yüklenirken hata:', error);
        toast.error(`${file.name} yüklenemedi`);
      }
    }
    
    setUploadingFiles(false);
    setSelectedFiles([]);
    return uploadedDocs;
  };

  const loadDocuments = async (itemType, itemId) => {
    try {
      const res = await axios.get(`${API}/branch/jobs/${selectedJob.id}/documents`, {
        params: { item_type: itemType, item_id: itemId },
        headers: getAuthHeaders()
      });
      return res.data || [];
    } catch (error) {
      console.error('Belgeler yüklenirken hata:', error);
      return [];
    }
  };

  const handlePreviewDocument = async (doc) => {
    const previewableTypes = ['.pdf', '.png', '.jpg', '.jpeg', '.gif'];
    if (previewableTypes.includes(doc.file_extension)) {
      setPreviewDoc(doc);
      setPreviewModalOpen(true);
    } else {
      handleDownloadDocument(doc);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.id}/download`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Belge indirilemedi');
    }
  };

  const handleDeleteDocument = async (docId, itemType, refreshCallback) => {
    try {
      await axios.delete(`${API}/documents/${docId}`, { headers: getAuthHeaders() });
      toast.success('Belge silindi');
      if (refreshCallback) refreshCallback();
    } catch (error) {
      toast.error('Belge silinemedi');
    }
  };

  const getFileIcon = (extension) => {
    const imageTypes = ['.png', '.jpg', '.jpeg', '.gif'];
    if (imageTypes.includes(extension)) {
      return <Image className="h-4 w-4 text-green-600" />;
    }
    if (extension === '.pdf') {
      return <FileText className="h-4 w-4 text-red-600" />;
    }
    return <File className="h-4 w-4 text-blue-600" />;
  };

  // Detay görüntüleme fonksiyonları
  const viewReportDetail = async (report) => {
    setSelectedReport(report);
    setReportDetailModal(true);
    try {
      const docs = await loadDocuments('report', report.id);
      setItemDocuments(docs);
    } catch (error) {
      setItemDocuments([]);
    }
  };

  const viewScheduleDetail = async (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleDetailModal(true);
    try {
      // İş programı detayını API'dan al (güncellemeler dahil)
      const res = await axios.get(`${API}/branch/jobs/${selectedJob.id}/schedules/${schedule.id}`, { headers: getAuthHeaders() });
      setSelectedSchedule(res.data);
      const docs = await loadDocuments('schedule', schedule.id);
      setItemDocuments(docs);
    } catch (error) {
      setItemDocuments([]);
    }
  };

  // İş programını tamamla
  const handleCompleteSchedule = async () => {
    if (!selectedSchedule || !selectedJob) return;
    
    setCompletingSchedule(true);
    try {
      await axios.put(`${API}/branch/jobs/${selectedJob.id}/schedules/${selectedSchedule.id}/complete`, {}, { headers: getAuthHeaders() });
      toast.success('İş programı tamamlandı');
      setScheduleDetailModal(false);
      openSchedulesModal(selectedJob);
    } catch (error) {
      console.error('İş programı tamamlama hatası:', error);
      toast.error('İş programı tamamlanamadı');
    }
    setCompletingSchedule(false);
  };

  // İş programı güncelleme modalını aç
  const openScheduleUpdateModal = () => {
    setScheduleUpdateText('');
    setScheduleUpdateFiles([]);
    setScheduleUpdateModal(true);
  };

  // İş programı güncelleme dosya seçimi
  const handleScheduleUpdateFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setScheduleUpdateFiles(prev => [...prev, ...files]);
  };

  // İş programı güncelleme dosyasını kaldır
  const removeScheduleUpdateFile = (index) => {
    setScheduleUpdateFiles(prev => prev.filter((_, i) => i !== index));
  };

  // İş programı güncellemesi gönder
  const handleSubmitScheduleUpdate = async () => {
    if (!scheduleUpdateText.trim()) {
      toast.error('Güncelleme açıklaması gerekli');
      return;
    }
    
    setSubmittingScheduleUpdate(true);
    try {
      // Güncelleme metnini gönder
      await axios.post(
        `${API}/branch/jobs/${selectedJob.id}/schedules/${selectedSchedule.id}/updates`,
        { description: scheduleUpdateText },
        { headers: getAuthHeaders() }
      );
      
      // Belgeleri yükle
      for (const file of scheduleUpdateFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('job_id', selectedJob.id);
        formData.append('item_type', 'schedule');
        formData.append('item_id', selectedSchedule.id);
        
        await axios.post(`${API}/upload/document`, formData, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success('Güncelleme eklendi');
      setScheduleUpdateModal(false);
      
      // İş programı detayını yenile
      const res = await axios.get(`${API}/branch/jobs/${selectedJob.id}/schedules/${selectedSchedule.id}`, { headers: getAuthHeaders() });
      setSelectedSchedule(res.data);
      const docs = await loadDocuments('schedule', selectedSchedule.id);
      setItemDocuments(docs);
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      toast.error('Güncelleme eklenemedi');
    }
    setSubmittingScheduleUpdate(false);
  };

  const viewTaskDetail = async (task) => {
    setSelectedTask(task);
    setTaskDetailModal(true);
    try {
      // Görev detayını API'dan al (güncellemeler dahil)
      const res = await axios.get(`${API}/branch/jobs/${selectedJob.id}/tasks/${task.id}`, { headers: getAuthHeaders() });
      setSelectedTask(res.data);
      const docs = await loadDocuments('task', task.id);
      setItemDocuments(docs);
    } catch (error) {
      setItemDocuments([]);
    }
  };

  // Görevi tamamla
  const handleCompleteTask = async () => {
    if (!selectedTask || !selectedJob) return;
    
    setCompletingTask(true);
    try {
      await axios.put(`${API}/branch/jobs/${selectedJob.id}/tasks/${selectedTask.id}/complete`, {}, { headers: getAuthHeaders() });
      toast.success('Görev tamamlandı');
      setTaskDetailModal(false);
      openTasksModal(selectedJob);
    } catch (error) {
      console.error('Görev tamamlama hatası:', error);
      toast.error('Görev tamamlanamadı');
    }
    setCompletingTask(false);
  };

  // Görev güncelleme modalını aç
  const openTaskUpdateModal = () => {
    setTaskUpdateText('');
    setTaskUpdateFiles([]);
    setTaskUpdateModal(true);
  };

  // Görev güncelleme dosya seçimi
  const handleTaskUpdateFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setTaskUpdateFiles(prev => [...prev, ...files]);
  };

  // Görev güncelleme dosyasını kaldır
  const removeTaskUpdateFile = (index) => {
    setTaskUpdateFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Görev güncellemesi gönder
  const handleSubmitTaskUpdate = async () => {
    if (!taskUpdateText.trim()) {
      toast.error('Güncelleme açıklaması gerekli');
      return;
    }
    
    setSubmittingTaskUpdate(true);
    try {
      // Güncelleme metnini gönder
      await axios.post(
        `${API}/branch/jobs/${selectedJob.id}/tasks/${selectedTask.id}/updates`,
        { description: taskUpdateText },
        { headers: getAuthHeaders() }
      );
      
      // Belgeleri yükle
      for (const file of taskUpdateFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('job_id', selectedJob.id);
        formData.append('item_type', 'task');
        formData.append('item_id', selectedTask.id);
        
        await axios.post(`${API}/upload/document`, formData, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success('Güncelleme eklendi');
      setTaskUpdateModal(false);
      
      // Görev detayını yenile
      const res = await axios.get(`${API}/branch/jobs/${selectedJob.id}/tasks/${selectedTask.id}`, { headers: getAuthHeaders() });
      setSelectedTask(res.data);
      const docs = await loadDocuments('task', selectedTask.id);
      setItemDocuments(docs);
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      toast.error('Güncelleme eklenemedi');
    }
    setSubmittingTaskUpdate(false);
  };

  // Belge yükleme bileşeni
  const DocumentUploader = ({ inputId = 'file-upload' }) => (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-amber-500 transition-colors">
      <input
        type="file"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        id={inputId}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg,.gif,.zip,.rar"
      />
      <label htmlFor={inputId} className="cursor-pointer">
        <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
        <p className="text-sm text-gray-600">Belge yüklemek için tıklayın</p>
        <p className="text-xs text-gray-400 mt-1">PDF, Word, Excel, Resim (max 10MB)</p>
      </label>
    </div>
  );

  // ============= İŞ DUVARI FONKSİYONLARI =============
  
  // İş Duvarı paylaşımlarını yükle
  const loadWallPosts = async (jobId) => {
    if (!jobId) return;
    setWallLoading(true);
    try {
      const res = await axios.get(`${API}/work/jobs/${jobId}/wall`, { headers: getAuthHeaders() });
      setWallPosts(res.data || []);
    } catch (error) {
      console.error('İş Duvarı yüklenemedi:', error);
      toast.error('İş Duvarı yüklenemedi');
    }
    setWallLoading(false);
  };

  // İş Duvarı modalını aç
  const openWallModal = (job) => {
    setSelectedJob(job);
    setWallModalOpen(true);
    setWallPostContent('');
    setWallPostDocuments([]);
    loadWallPosts(job.id);
  };

  // İş Duvarı dosya yükleme
  const handleWallFileUpload = async (e, isComment = false) => {
    const files = Array.from(e.target.files || []);
    const newDocs = [];
    
    for (const file of files) {
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      newDocs.push({
        name: file.name,
        type: file.type,
        data: base64
      });
    }
    
    if (isComment) {
      setWallCommentDocuments(prev => [...prev, ...newDocs]);
    } else {
      setWallPostDocuments(prev => [...prev, ...newDocs]);
    }
  };

  // Yeni paylaşım oluştur
  const handleCreateWallPost = async () => {
    if (!wallPostContent.trim()) {
      toast.error('Paylaşım içeriği gerekli');
      return;
    }
    
    setWallSubmitting(true);
    try {
      await axios.post(`${API}/work/jobs/${selectedJob.id}/wall`, {
        content: wallPostContent,
        documents: wallPostDocuments
      }, { headers: getAuthHeaders() });
      
      toast.success('Paylaşım oluşturuldu');
      setWallPostContent('');
      setWallPostDocuments([]);
      loadWallPosts(selectedJob.id);
    } catch (error) {
      console.error('Paylaşım hatası:', error);
      toast.error('Paylaşım oluşturulamadı');
    }
    setWallSubmitting(false);
  };

  // Paylaşım detayını aç
  const openWallPostDetail = async (post) => {
    try {
      const res = await axios.get(`${API}/work/jobs/${selectedJob.id}/wall/${post.id}`, { headers: getAuthHeaders() });
      setSelectedWallPost(res.data);
      setWallDetailModalOpen(true);
      setWallCommentContent('');
      setWallCommentDocuments([]);
      loadWallPosts(selectedJob.id);
    } catch (error) {
      console.error('Detay yüklenemedi:', error);
      toast.error('Detay yüklenemedi');
    }
  };

  // Yorum ekle
  const handleAddWallComment = async () => {
    if (!wallCommentContent.trim()) {
      toast.error('Yorum yazın');
      return;
    }
    
    setWallSubmitting(true);
    try {
      await axios.post(`${API}/work/jobs/${selectedJob.id}/wall/${selectedWallPost.id}/comments`, {
        content: wallCommentContent,
        documents: wallCommentDocuments
      }, { headers: getAuthHeaders() });
      
      toast.success('Yorum eklendi');
      setWallCommentContent('');
      setWallCommentDocuments([]);
      
      const res = await axios.get(`${API}/work/jobs/${selectedJob.id}/wall/${selectedWallPost.id}`, { headers: getAuthHeaders() });
      setSelectedWallPost(res.data);
    } catch (error) {
      console.error('Yorum hatası:', error);
      toast.error('Yorum eklenemedi');
    }
    setWallSubmitting(false);
  };

  // Paylaşım sil
  const handleDeleteWallPost = async (postId) => {
    if (!window.confirm('Bu paylaşımı silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/work/jobs/${selectedJob.id}/wall/${postId}`, { headers: getAuthHeaders() });
      toast.success('Paylaşım silindi');
      loadWallPosts(selectedJob.id);
      if (wallDetailModalOpen) {
        setWallDetailModalOpen(false);
        setSelectedWallPost(null);
      }
    } catch (error) {
      console.error('Silme hatası:', error);
      toast.error('Silinemedi');
    }
  };

  // Yorum sil
  const handleDeleteWallComment = async (commentId) => {
    if (!window.confirm('Bu yorumu silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/work/jobs/${selectedJob.id}/wall/comments/${commentId}`, { headers: getAuthHeaders() });
      toast.success('Yorum silindi');
      
      const res = await axios.get(`${API}/work/jobs/${selectedJob.id}/wall/${selectedWallPost.id}`, { headers: getAuthHeaders() });
      setSelectedWallPost(res.data);
    } catch (error) {
      console.error('Silme hatası:', error);
      toast.error('Silinemedi');
    }
  };

  // Paylaşım düzenle modal aç
  const openEditWallPostModal = (post) => {
    setEditingWallPost(post);
    setWallPostContent(post.content || '');
    setWallPostDocuments(post.documents || []);
    setEditWallPostModalOpen(true);
  };

  // Paylaşım güncelle
  const handleUpdateWallPost = async () => {
    if (!wallPostContent.trim()) {
      toast.error('Paylaşım içeriği gerekli');
      return;
    }
    
    setWallSubmitting(true);
    try {
      await axios.put(`${API}/work/jobs/${selectedJob.id}/wall/${editingWallPost.id}`, {
        content: wallPostContent,
        documents: wallPostDocuments
      }, { headers: getAuthHeaders() });
      
      toast.success('Paylaşım güncellendi');
      setEditWallPostModalOpen(false);
      setEditingWallPost(null);
      setWallPostContent('');
      setWallPostDocuments([]);
      loadWallPosts(selectedJob.id);
      
      if (wallDetailModalOpen && selectedWallPost?.id === editingWallPost.id) {
        const res = await axios.get(`${API}/work/jobs/${selectedJob.id}/wall/${editingWallPost.id}`, { headers: getAuthHeaders() });
        setSelectedWallPost(res.data);
      }
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      toast.error('Güncellenemedi');
    }
    setWallSubmitting(false);
  };

  // Yorum düzenle modal aç
  const openEditWallCommentModal = (comment) => {
    setEditingWallComment(comment);
    setWallCommentContent(comment.content || '');
    setWallCommentDocuments(comment.documents || []);
    setEditWallCommentModalOpen(true);
  };

  // Yorum güncelle
  const handleUpdateWallComment = async () => {
    if (!wallCommentContent.trim()) {
      toast.error('Yorum içeriği gerekli');
      return;
    }
    
    setWallSubmitting(true);
    try {
      await axios.put(`${API}/work/jobs/${selectedJob.id}/wall/comments/${editingWallComment.id}`, {
        content: wallCommentContent,
        documents: wallCommentDocuments
      }, { headers: getAuthHeaders() });
      
      toast.success('Yorum güncellendi');
      setEditWallCommentModalOpen(false);
      setEditingWallComment(null);
      setWallCommentContent('');
      setWallCommentDocuments([]);
      
      const res = await axios.get(`${API}/work/jobs/${selectedJob.id}/wall/${selectedWallPost.id}`, { headers: getAuthHeaders() });
      setSelectedWallPost(res.data);
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      toast.error('Güncellenemedi');
    }
    setWallSubmitting(false);
  };

  // Seçili dosyalar listesi
  const SelectedFilesList = () => (
    selectedFiles.length > 0 && (
      <div className="mt-3 space-y-2">
        <Label className="text-sm text-gray-600">Seçili Belgeler:</Label>
        {selectedFiles.map((file, index) => (
          <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
            <div className="flex items-center gap-2">
              {getFileIcon(`.${file.name.split('.').pop()}`)}
              <span className="text-sm truncate max-w-[200px]">{file.name}</span>
              <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
            </div>
            <Button size="sm" variant="ghost" onClick={() => removeSelectedFile(index)}>
              <X className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        ))}
      </div>
    )
  );

  // Belge listesi bileşeni
  const DocumentsList = ({ documents, onDelete, onRefresh, readOnly = false }) => (
    documents && documents.length > 0 && (
      <div className="mt-4 border-t pt-4">
        <Label className="text-sm font-medium flex items-center gap-2 mb-2">
          <FileIcon className="h-4 w-4" />
          Ekli Belgeler ({documents.length})
        </Label>
        <div className="space-y-2">
          {documents.map((doc) => (
            <div key={doc.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
              <div className="flex items-center gap-2">
                {getFileIcon(doc.file_extension)}
                <span className="text-sm truncate max-w-[200px]">{doc.original_filename}</span>
                <span className="text-xs text-gray-400">({(doc.file_size / 1024).toFixed(1)} KB)</span>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => handlePreviewDocument(doc)} title="Önizle">
                  <Eye className="h-4 w-4 text-blue-600" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDownloadDocument(doc)} title="İndir">
                  <Download className="h-4 w-4 text-green-600" />
                </Button>
                {!readOnly && (
                  <Button size="sm" variant="ghost" onClick={() => handleDeleteDocument(doc.id, doc.item_type, onRefresh)} title="Sil">
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  );

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high':
        return <Badge className="bg-red-100 text-red-700">Yüksek</Badge>;
      case 'normal':
        return <Badge className="bg-blue-100 text-blue-700">Normal</Badge>;
      case 'low':
        return <Badge className="bg-gray-100 text-gray-700">Düşük</Badge>;
      default:
        return <Badge variant="outline">{priority}</Badge>;
    }
  };

  const getTaskStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700">Bekliyor</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-700">Devam Ediyor</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-700">Tamamlandı</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Form content renderer
  const renderFormContent = (isEdit) => (
    <div className="space-y-4 py-4">
      {/* İşin Adı */}
      <div className="space-y-2">
        <Label>İşin Adı *</Label>
        <Input 
          value={formData.name} 
          onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))} 
          placeholder="İş adını girin"
          data-testid="job-name-input"
        />
      </div>

      {/* İşi Takip Eden Kişiler */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Users className="h-4 w-4" />
          İşi Takip Eden Kişiler
        </Label>
        <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2" data-testid="tracking-users-list">
          {trackingUsers.length === 0 ? (
            <p className="text-sm text-gray-500">Kullanıcı bulunamadı</p>
          ) : (
            trackingUsers.map(user => (
              <div key={user.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`user-${user.id}-${isEdit ? 'edit' : 'create'}`}
                  checked={formData.tracking_users.includes(user.id)}
                  onCheckedChange={() => handleUserToggle(user.id)}
                  data-testid={`tracking-user-checkbox-${user.id}`}
                />
                <label htmlFor={`user-${user.id}-${isEdit ? 'edit' : 'create'}`} className="text-sm cursor-pointer flex items-center gap-2 flex-wrap">
                  <span>{user.name}</span>
                  {(user.panel_label || user.branch_name) && (
                    <span className="text-[11px] text-gray-400">
                      {[user.panel_label, user.branch_name].filter(Boolean).join(' • ')}
                    </span>
                  )}
                </label>
              </div>
            ))
          )}
        </div>
        {formData.tracking_users.length > 0 && (
          <p className="text-xs text-gray-500">{formData.tracking_users.length} kişi seçildi</p>
        )}
      </div>

      {/* İşi Yapan Şirket */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          İşi Yapan Şirket *
        </Label>
        <Select 
          value={companySelectValue} 
          onValueChange={handleCompanySelect}
          data-testid="job-company-select"
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Şirket seçin" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fortis">Fortis</SelectItem>
            {subcontractors.map((sub) => (
              <SelectItem key={sub.id} value={sub.id}>{sub.name}</SelectItem>
            ))}
            <SelectItem value="diger">Diğer (Elle Yaz)</SelectItem>
          </SelectContent>
        </Select>
        {companySelectValue === 'diger' && (
          <Input 
            value={customCompanyName} 
            onChange={(e) => handleCustomCompanyChange(e.target.value)} 
            placeholder="Şirket adını girin"
            className="mt-2"
            data-testid="job-company-custom-input"
          />
        )}
      </div>

      {/* Tarihler */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Başlangıç Tarihi *
          </Label>
          <Input 
            type="date" 
            value={formData.start_date} 
            onChange={(e) => setFormData(prev => ({...prev, start_date: e.target.value}))}
            data-testid="job-start-date-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Tahmini Bitiş Tarihi *
          </Label>
          <Input 
            type="date" 
            value={formData.estimated_end_date} 
            onChange={(e) => setFormData(prev => ({...prev, estimated_end_date: e.target.value}))}
            data-testid="job-end-date-input"
          />
        </div>
      </div>

      {/* Konumlar */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          Konum(lar)
        </Label>
        <div className="space-y-2">
          {formData.locations.map((location, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                value={location}
                onChange={(e) => updateLocation(index, e.target.value)}
                placeholder={`Konum ${index + 1}`}
                data-testid={`job-location-input-${index}`}
              />
              {formData.locations.length > 1 && (
                <Button 
                  type="button" 
                  variant="outline" 
                  size="icon"
                  onClick={() => removeLocation(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          <Button 
            type="button" 
            variant="outline" 
            size="sm" 
            onClick={addLocation}
            className="mt-2"
          >
            <Plus className="h-4 w-4 mr-1" />
            Konum Ekle
          </Button>
        </div>
      </div>

      {/* Açıklama */}
      <div className="space-y-2">
        <Label>Açıklama</Label>
        <Textarea 
          value={formData.description} 
          onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))} 
          placeholder="İş hakkında açıklama..."
          rows={3}
          data-testid="job-description-input"
        />
      </div>

      {/* İş ile Alakalı Belgeler (sadece oluşturma modunda) */}
      {!isEdit && (
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <File className="h-4 w-4" />
            İş ile Alakalı Belgeler
          </Label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
            <input
              type="file"
              multiple
              onChange={handleJobDocumentSelect}
              className="hidden"
              id="job-document-upload"
              data-testid="job-document-input"
            />
            <label 
              htmlFor="job-document-upload" 
              className="flex flex-col items-center cursor-pointer"
            >
              <Upload className="h-8 w-8 text-gray-400 mb-2" />
              <span className="text-sm text-gray-600">Belge yüklemek için tıklayın</span>
              <span className="text-xs text-gray-400 mt-1">PDF, Word, Excel, resim dosyaları</span>
            </label>
          </div>
          
          {/* Seçilen belgeler listesi */}
          {pendingJobDocuments.length > 0 && (
            <div className="space-y-2 mt-3">
              <p className="text-sm font-medium text-gray-700">{pendingJobDocuments.length} belge seçildi:</p>
              {pendingJobDocuments.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-md">
                  <div className="flex items-center gap-2">
                    <FileIcon className="h-4 w-4 text-amber-600" />
                    <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                    <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removePendingDocument(index)}
                    className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Durum (sadece düzenleme modunda) */}
      {isEdit && (
        <div className="space-y-2">
          <Label>Durum</Label>
          <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({...prev, status: value}))}>
            <SelectTrigger data-testid="job-status-select">
              <SelectValue placeholder="Durum seçin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Devam Ediyor</SelectItem>
              <SelectItem value="completed">Tamamlandı</SelectItem>
              <SelectItem value="cancelled">İptal Edildi</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );

  // Tablo render
  const renderJobsTable = (jobsList, isArchive = false) => {
    if (jobsList.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500">
          {isArchive ? (
            <>
              <Archive className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Arşivde iş bulunmuyor</h3>
              <p className="text-gray-500">Arşivlenen işler burada görünecek</p>
            </>
          ) : (
            <>
              <Briefcase className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz iş bulunmuyor</h3>
              <p className="text-gray-500 mb-4">İlk işi eklemek için yukarıdaki butonu kullanın</p>
            </>
          )}
        </div>
      );
    }

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleSort('name')} data-testid="sort-name">
              <div className="flex items-center gap-2">İş Adı {getSortIcon('name')}</div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleSort('company')} data-testid="sort-company">
              <div className="flex items-center gap-2">Şirket {getSortIcon('company')}</div>
            </TableHead>
            <TableHead>Takip Edenler</TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleSort('start_date')} data-testid="sort-start">
              <div className="flex items-center gap-2">Başlangıç {getSortIcon('start_date')}</div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleSort('estimated_end_date')} data-testid="sort-end">
              <div className="flex items-center gap-2">Tahmini Bitiş {getSortIcon('estimated_end_date')}</div>
            </TableHead>
            <TableHead>Konum(lar)</TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleSort('progress')} data-testid="sort-progress">
              <div className="flex items-center gap-2">İş İlerleme {getSortIcon('progress')}</div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleSort('status')} data-testid="sort-status">
              <div className="flex items-center gap-2">Durum {getSortIcon('status')}</div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleSort('updated_at')} data-testid="sort-updated">
              <div className="flex items-center gap-2">Son Güncelleme {getSortIcon('updated_at')}</div>
            </TableHead>
            {isArchive && (
              <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleSort('archived_at')} data-testid="sort-archived">
                <div className="flex items-center gap-2">Arşivlenme {getSortIcon('archived_at')}</div>
              </TableHead>
            )}
            <TableHead>İşlemler</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobsList.map(job => {
            const isUnseenJob = !isArchive && !job.is_seen;
            const isCancelled = job.status === 'cancelled';
            
            // Cell background for cancelled jobs
            const cellBg = isCancelled ? 'bg-red-200' : '';
            
            return (
            <TableRow 
              key={job.id} 
              data-testid={`job-row-${job.id}`} 
              className={isUnseenJob && !isCancelled ? "bg-yellow-50/50" : ""}
            >
              <TableCell className={`font-medium ${cellBg}`}>
                <div className="flex items-center gap-2">
                  {job.name}
                  {isUnseenJob && (
                    <Badge className="bg-red-500 text-white text-xs px-1.5 py-0.5 animate-pulse">
                      YENİ
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className={cellBg}>
                <div className="flex items-center gap-1">
                  <Building2 className="h-4 w-4 text-gray-400" />
                  {job.company}
                </div>
              </TableCell>
              <TableCell className={cellBg}>
                <div className="flex flex-wrap gap-1">
                  {job.tracking_user_details?.length > 0 ? (
                    job.tracking_user_details.slice(0, 2).map((user, idx) => {
                      const isAccepted = job.accepted_by?.some(a => a.user_id === user.id);
                      return (
                        <Badge 
                          key={idx} 
                          variant="outline" 
                          className={`text-xs ${isAccepted ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}
                        >
                          {isAccepted ? (
                            <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
                          ) : (
                            <XCircle className="h-3 w-3 mr-1 text-red-500" />
                          )}
                          {user.name}
                        </Badge>
                      );
                    })
                  ) : (
                    <span className="text-gray-400 text-sm">-</span>
                  )}
                  {job.tracking_user_details?.length > 2 && (
                    <Badge variant="outline" className="text-xs">
                      +{job.tracking_user_details.length - 2}
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className={cellBg}>{job.start_date}</TableCell>
              <TableCell className={cellBg}>{job.estimated_end_date}</TableCell>
              <TableCell className={cellBg}>
                <div className="flex flex-wrap gap-1">
                  {job.locations?.length > 0 ? (
                    job.locations.slice(0, 2).map((loc, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        <MapPin className="h-3 w-3 mr-1" />
                        {loc}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">-</span>
                  )}
                  {job.locations?.length > 2 && (
                    <Badge variant="secondary" className="text-xs">
                      +{job.locations.length - 2}
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className={cellBg}>
                <div className="w-24">
                  <div className="flex items-center gap-2 mb-1">
                    <Percent className="h-3 w-3 text-gray-500" />
                    <span className="text-sm font-medium">{job.progress || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getProgressColor(job.progress || 0)}`}
                      style={{ width: `${job.progress || 0}%` }}
                    />
                  </div>
                </div>
              </TableCell>
              <TableCell className={cellBg}>
                <div className="flex flex-col gap-1">
                  {getStatusBadge(job.status)}
                  {job.forwarded_to && (
                    <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-xs">
                      <Send className="h-2.5 w-2.5 mr-1" />
                      {job.forwarded_to === 'purchasing' ? 'Satın Alma' : 
                       job.forwarded_to === 'hr' ? 'İK' :
                       job.forwarded_to === 'accounting' ? 'Muhasebe' :
                       job.forwarded_to === 'admin' ? 'İdari İşler' : job.forwarded_to}
                    </Badge>
                  )}
                  {/* İptal bilgileri */}
                  {job.status === 'cancelled' && (job.cancelled_by_name || job.cancel_reason) && (
                    <div className="text-xs mt-1 space-y-0.5">
                      {job.cancelled_by_name && (
                        <div className="text-red-600 font-medium">
                          İptal Eden: {job.cancelled_by_name}
                        </div>
                      )}
                      {job.cancel_reason && (
                        <div className="text-gray-600 italic max-w-[200px] truncate" title={job.cancel_reason}>
                          Sebep: {job.cancel_reason}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell className={cellBg}>
                <div className="text-sm text-gray-600">
                  {job.updated_at ? (
                    <>
                      <div>{new Date(job.updated_at).toLocaleDateString('tr-TR')}</div>
                      <div className="text-xs text-gray-400">{new Date(job.updated_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</div>
                    </>
                  ) : '-'}
                </div>
              </TableCell>
              {isArchive && (
                <TableCell className={cellBg}>
                  <div className="text-sm text-gray-600">
                    {job.archived_at ? new Date(job.archived_at).toLocaleDateString('tr-TR') : '-'}
                    {job.archived_by_name && (
                      <div className="text-xs text-gray-400">{job.archived_by_name}</div>
                    )}
                  </div>
                </TableCell>
              )}
              <TableCell className={cellBg}>
                <TooltipProvider>
                  <div className="flex gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => {
                            // Görüntülendi olarak işaretle (sadece arşiv değilse ve görülmemişse)
                            if (!isArchive && !job.is_seen) {
                              markJobAsSeen(job.id);
                            }
                            openDetailModal(job);
                          }}
                          data-testid={`view-job-${job.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Detay</TooltipContent>
                    </Tooltip>
                    
                    {!isArchive && (
                      <>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-purple-600 hover:text-purple-700 relative"
                              onClick={() => openReportsModal(job)}
                              data-testid={`reports-job-${job.id}`}
                            >
                              <FileText className="h-4 w-4" />
                              {job.new_reports_count > 0 && (
                                <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                                  {job.new_reports_count}
                                </span>
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Raporlar ({job.reports_count || 0}){job.new_reports_count > 0 && ` - ${job.new_reports_count} yeni`}</TooltipContent>
                        </Tooltip>
                        
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-blue-600 hover:text-blue-700 relative"
                              onClick={() => openSchedulesModal(job)}
                              data-testid={`schedules-job-${job.id}`}
                            >
                              <CalendarDays className="h-4 w-4" />
                              {job.new_schedules_count > 0 && (
                                <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                                  {job.new_schedules_count}
                                </span>
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>İş Programı ({job.schedules_count || 0}){job.new_schedules_count > 0 && ` - ${job.new_schedules_count} yeni`}</TooltipContent>
                        </Tooltip>
                        
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-green-600 hover:text-green-700 relative"
                              onClick={() => openTasksModal(job)}
                              data-testid={`tasks-job-${job.id}`}
                            >
                              <ClipboardList className="h-4 w-4" />
                              {job.new_tasks_count > 0 && (
                                <span className="absolute -top-1 -right-1 bg-green-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                                  {job.new_tasks_count}
                                </span>
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Görevler ({job.tasks_count || 0}){job.new_tasks_count > 0 && ` - ${job.new_tasks_count} yeni`}</TooltipContent>
                        </Tooltip>
                        
                        {/* İş Duvarı butonu */}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 relative"
                              onClick={() => openWallModal(job)}
                              data-testid={`wall-job-${job.id}`}
                            >
                              <StickyNote className="h-4 w-4" />
                              {job.new_wall_count > 0 && (
                                <span className="absolute -top-1 -right-1 bg-amber-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                                  {job.new_wall_count}
                                </span>
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>İş Duvarı ({job.wall_posts_count || 0}){job.new_wall_count > 0 && ` - ${job.new_wall_count} yeni`}</TooltipContent>
                        </Tooltip>
                        
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => openEditModal(job)}
                              data-testid={`edit-job-${job.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Düzenle</TooltipContent>
                        </Tooltip>
                        
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className={`${job.forwarded_to ? 'text-gray-400' : 'text-indigo-600 hover:text-indigo-700'}`}
                              onClick={() => openForwardModal(job)}
                              disabled={!!job.forwarded_to}
                              data-testid={`forward-job-${job.id}`}
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {job.forwarded_to 
                              ? `İletildi (${job.forwarded_to === 'purchasing' ? 'Satın Alma' : job.forwarded_to})`
                              : 'İlet'
                            }
                          </TooltipContent>
                        </Tooltip>
                        
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-orange-600"
                              onClick={() => openArchiveModal(job)}
                              data-testid={`archive-job-${job.id}`}
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Arşive Gönder</TooltipContent>
                        </Tooltip>
                      </>
                    )}
                    
                    {isArchive && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-green-600 hover:text-green-700"
                            onClick={() => handleRestoreJob(job.id)}
                            data-testid={`restore-job-${job.id}`}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Geri Yükle</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TooltipProvider>
              </TableCell>
            </TableRow>
          );
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="space-y-6" data-testid="branch-jobs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Briefcase className="h-8 w-8 text-amber-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">İşler</h1>
            <p className="text-gray-500">Şube işlerini yönetin</p>
          </div>
        </div>
        {activeTab === 'active' && (
          <Button 
            onClick={() => { resetForm(); setCreateModalOpen(true); }} 
            className="bg-amber-600 hover:bg-amber-700 gap-2"
            data-testid="add-job-btn"
          >
            <Plus className="h-4 w-4" />
            İŞ EKLE
          </Button>
        )}
        {activeTab === 'talepler' && (
          <Button 
            onClick={() => setTalepModalOpen(true)} 
            className="bg-purple-600 hover:bg-purple-700 gap-2"
            data-testid="add-talep-btn"
          >
            <Plus className="h-4 w-4" />
            TALEP EKLE
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          <TabsTrigger value="active" className="gap-2" data-testid="tab-active">
            <Briefcase className="h-4 w-4" />
            AKTİF İŞLER
            {jobs.filter(j => !j.is_seen).length > 0 && (
              <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 min-w-[20px] h-5 animate-pulse">
                {jobs.filter(j => !j.is_seen).length}
              </Badge>
            )}
          </TabsTrigger>
          {/* TALEPLER TAB - ŞİMDİLİK KALDIRILDI */}
          <TabsTrigger value="archive" className="gap-2" data-testid="tab-archive">
            <Archive className="h-4 w-4" />
            ARŞİV
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          {/* Bilgilendirme */}
          <InfoBanner id="branch-jobs-active-info" variant="info" className="mt-4">
            <strong>Aktif İşler:</strong> İş ekle tuşu ile ekleyeceğiniz veya başka bir kullanıcının oluşturduğu işleri buradan görüntüleyebilirsiniz. 
            İnteraktif bir şekilde iş ile alakalı raporları yükleyebilir, yeni raporlar ekleyebilirsiniz. 
            Yeni görev atamaları yapabilirsiniz, iş programlarını yükleyip güncelleyebilirsiniz. 
            Bitirilen işleri arşive taşıyabilirsiniz.
          </InfoBanner>

          {/* Alt Tablar - Aktif İşler, Ara Verilen, Tamamlanan, İptal Edilen, İletilenler */}
          <div className="flex gap-2 mt-4 border-b flex-wrap">
            <button
              onClick={() => setActiveSubTab('active')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeSubTab === 'active'
                  ? 'text-amber-600 border-b-2 border-amber-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="subtab-active"
            >
              Aktif İşler
              <Badge variant="outline" className="ml-2">{activeJobsCount}</Badge>
              {unseenActiveCount > 0 && (
                <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 animate-pulse">
                  {unseenActiveCount} YENİ
                </Badge>
              )}
            </button>
            <button
              onClick={() => setActiveSubTab('paused')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeSubTab === 'paused'
                  ? 'text-yellow-600 border-b-2 border-yellow-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="subtab-paused"
            >
              Ara Verilen İşler
              <Badge variant="outline" className="ml-2 bg-yellow-100 text-yellow-700">{pausedJobsCount}</Badge>
              {unseenPausedCount > 0 && (
                <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 animate-pulse">
                  {unseenPausedCount} YENİ
                </Badge>
              )}
            </button>
            <button
              onClick={() => setActiveSubTab('completed')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeSubTab === 'completed'
                  ? 'text-green-600 border-b-2 border-green-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="subtab-completed"
            >
              Tamamlanan İşler
              <Badge variant="outline" className="ml-2 bg-green-100 text-green-700">{completedJobsCount}</Badge>
              {unseenCompletedCount > 0 && (
                <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 animate-pulse">
                  {unseenCompletedCount} YENİ
                </Badge>
              )}
            </button>
            <button
              onClick={() => setActiveSubTab('cancelled')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeSubTab === 'cancelled'
                  ? 'text-red-600 border-b-2 border-red-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="subtab-cancelled"
            >
              İptal Edilen İşler
              <Badge variant="outline" className="ml-2 bg-red-100 text-red-700">{cancelledJobsCount}</Badge>
              {unseenCancelledCount > 0 && (
                <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 animate-pulse">
                  {unseenCancelledCount} YENİ
                </Badge>
              )}
            </button>
            <button
              onClick={() => setActiveSubTab('forwarded')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeSubTab === 'forwarded'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="subtab-forwarded"
            >
              İletilenler
              {forwardedJobsCount > 0 && (
                <Badge className="ml-2 bg-indigo-100 text-indigo-700">{forwardedJobsCount}</Badge>
              )}
              {unseenForwardedCount > 0 && (
                <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 animate-pulse">
                  {unseenForwardedCount} YENİ
                </Badge>
              )}
            </button>
          </div>

          {/* Filtreler */}
          <Card className="mt-4">
            <CardContent className="py-4">
              <div className="flex items-center gap-4 flex-wrap">
                <Label>Durum Filtresi:</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-48" data-testid="status-filter">
                    <SelectValue placeholder="Tümü" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="active">Devam Ediyor</SelectItem>
                    <SelectItem value="completed">Tamamlandı</SelectItem>
                    <SelectItem value="cancelled">İptal Edildi</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* İletilen Yer Filtresi - Sadece İletilenler tabında görünür */}
                {activeSubTab === 'forwarded' && (
                  <>
                    <Label className="ml-4">İletilen Yer:</Label>
                    <Select value={forwardedToFilter} onValueChange={setForwardedToFilter}>
                      <SelectTrigger className="w-48" data-testid="forwarded-to-filter">
                        <SelectValue placeholder="Tümü" />
                      </SelectTrigger>
                      <SelectContent>
                        {forwardedToOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                )}
                
                <Badge variant="outline" className="ml-auto">
                  {filteredJobs.length} iş
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* İşler Tablosu */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-amber-600" />
                İş Listesi
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
                </div>
              ) : (
                renderJobsTable(filteredJobs, false)
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TALEPLER TAB - ŞİMDİLİK KALDIRILDI */}

        {/* Arşiv Tab */}
        <TabsContent value="archive">
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Archive className="h-5 w-5 text-gray-600" />
                Arşivlenmiş İşler
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
                </div>
              ) : (
                renderJobsTable(archivedJobs, true)
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-amber-600" />
              Yeni İş Ekle
            </DialogTitle>
          </DialogHeader>
          {renderFormContent(false)}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCreateJob} 
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="create-job-btn"
            >
              Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-amber-600" />
              İş Düzenle
            </DialogTitle>
          </DialogHeader>
          {renderFormContent(true)}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleEditJob} 
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="save-job-btn"
            >
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-amber-600" />
              İş Detayı
            </DialogTitle>
          </DialogHeader>
          {selectedJob && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">İş Adı</Label>
                  <p className="font-medium text-lg">{selectedJob.name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Durum</Label>
                  <div className="mt-1">{getStatusBadge(selectedJob.status)}</div>
                </div>
              </div>
              
              {/* Durum Güncelleme Bölümü - Sadece arşiv değilse göster */}
              {!selectedJob.is_archived && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <Label className="text-blue-700 flex items-center gap-2 mb-3">
                    <RefreshCcw className="h-4 w-4" />
                    Durum Güncelle
                  </Label>
                  <div className="space-y-3">
                    <Select 
                      value={newStatus || selectedJob.status || 'active'} 
                      onValueChange={(value) => setNewStatus(value)}
                    >
                      <SelectTrigger data-testid="detail-status-select">
                        <SelectValue placeholder="Durum seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            Devam Ediyor
                          </div>
                        </SelectItem>
                        <SelectItem value="paused">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                            Ara Verildi
                          </div>
                        </SelectItem>
                        <SelectItem value="completed">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            Tamamlandı
                          </div>
                        </SelectItem>
                        <SelectItem value="cancelled">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            İptal Edildi
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <Textarea
                      placeholder="Durum açıklaması (opsiyonel)..."
                      value={statusDescription}
                      onChange={(e) => setStatusDescription(e.target.value)}
                      rows={2}
                      className="bg-white"
                      data-testid="detail-status-description"
                    />
                    <Button 
                      onClick={() => handleStatusUpdate(selectedJob)}
                      disabled={updatingStatus || !newStatus}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      data-testid="detail-save-status-btn"
                    >
                      {updatingStatus ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      )}
                      Durumu Kaydet
                    </Button>
                    {selectedJob.status_description && (
                      <p className="text-xs text-blue-600 mt-2">
                        <span className="font-medium">Son Açıklama:</span> {selectedJob.status_description}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* İptal Bilgileri - Sadece iptal edilmiş işler için */}
              {selectedJob?.status === 'cancelled' && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <h4 className="font-medium text-red-800 flex items-center gap-2 mb-3">
                    <Ban className="h-4 w-4" />
                    İptal Bilgileri
                  </h4>
                  <div className="space-y-2 text-sm">
                    {selectedJob.cancelled_by_name && (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-red-600" />
                        <span className="text-gray-600">İptal Eden:</span>
                        <span className="font-medium text-red-700">{selectedJob.cancelled_by_name}</span>
                      </div>
                    )}
                    {selectedJob.cancelled_at && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-red-600" />
                        <span className="text-gray-600">İptal Tarihi:</span>
                        <span className="font-medium text-red-700">
                          {new Date(selectedJob.cancelled_at).toLocaleString('tr-TR')}
                        </span>
                      </div>
                    )}
                    {selectedJob.cancel_reason && (
                      <div className="mt-3 pt-3 border-t border-red-200">
                        <span className="text-gray-600 block mb-1">İptal Nedeni:</span>
                        <p className="text-red-700 bg-white p-2 rounded border border-red-100">
                          {selectedJob.cancel_reason}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* İlerleme */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-gray-500 flex items-center gap-2">
                    <Percent className="h-4 w-4" />
                    İş İlerleme
                  </Label>
                  <span className="text-lg font-bold text-amber-600">{selectedJob.progress || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                  <div 
                    className={`h-3 rounded-full ${getProgressColor(selectedJob.progress || 0)}`}
                    style={{ width: `${selectedJob.progress || 0}%` }}
                  />
                </div>
                <Button 
                  onClick={() => openProgressModal(selectedJob)}
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  data-testid="update-progress-btn"
                  disabled={selectedJob.status !== 'active'}
                >
                  <Percent className="h-4 w-4 mr-2" />
                  İlerleme Seviyesi Gir
                </Button>
              </div>
              
              <div>
                <Label className="text-gray-500">İşi Yapan Şirket</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Building2 className="h-4 w-4 text-amber-600" />
                  <p className="font-medium">{selectedJob.company}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Başlangıç Tarihi</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="h-4 w-4 text-amber-600" />
                    <p>{selectedJob.start_date}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-500">Tahmini Bitiş Tarihi</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="h-4 w-4 text-amber-600" />
                    <p>{selectedJob.estimated_end_date}</p>
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-500">İşi Takip Edenler</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedJob.tracking_user_details?.length > 0 ? (
                    selectedJob.tracking_user_details.map((user, idx) => {
                      const isAccepted = selectedJob.accepted_by?.some(a => a.user_id === user.id);
                      return (
                        <Badge 
                          key={idx} 
                          className={isAccepted ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
                        >
                          {isAccepted ? (
                            <CheckCircle className="h-3 w-3 mr-1" />
                          ) : (
                            <XCircle className="h-3 w-3 mr-1" />
                          )}
                          {user.name}
                        </Badge>
                      );
                    })
                  ) : (
                    <p className="text-gray-400">Takip eden yok</p>
                  )}
                </div>
              </div>

              {/* Kabul Edenler Bölümü */}
              {selectedJob.accepted_by?.length > 0 && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <Label className="text-green-700 flex items-center gap-2 mb-3">
                    <CheckCircle className="h-4 w-4" />
                    Kabul Edenler ({selectedJob.accepted_by.length})
                  </Label>
                  <div className="space-y-2">
                    {selectedJob.accepted_by.map((acceptance, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white p-2 rounded-md border border-green-100">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="font-medium">{acceptance.user_name}</span>
                        </div>
                        <span className="text-sm text-gray-500">
                          {acceptance.accepted_at 
                            ? new Date(acceptance.accepted_at).toLocaleString('tr-TR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : '-'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Ara Verme Bilgileri - Eğer iş paused durumundaysa */}
              {selectedJob.status === 'paused' && selectedJob.pause_info && (
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <Label className="text-yellow-700 flex items-center gap-2 mb-3">
                    <AlertCircle className="h-4 w-4" />
                    Ara Verme Bilgileri
                  </Label>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between bg-white p-3 rounded-md border border-yellow-100">
                      <div>
                        <p className="text-sm text-gray-500">Ara Veren</p>
                        <p className="font-medium">{selectedJob.pause_info.paused_by_name || '-'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Tarih</p>
                        <p className="font-medium">
                          {selectedJob.pause_info.paused_at 
                            ? new Date(selectedJob.pause_info.paused_at).toLocaleString('tr-TR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : '-'}
                        </p>
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded-md border border-yellow-100">
                      <p className="text-sm text-gray-500 mb-1">Ara Verme Sebebi</p>
                      <p className="text-gray-800 whitespace-pre-wrap">{selectedJob.pause_info.reason || '-'}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <Label className="text-gray-500">Konum(lar)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedJob.locations?.length > 0 ? (
                    selectedJob.locations.map((loc, idx) => (
                      <Badge key={idx} variant="secondary">
                        <MapPin className="h-3 w-3 mr-1" />
                        {loc}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-gray-400">Konum belirtilmemiş</p>
                  )}
                </div>
              </div>
              
              {selectedJob.description && (
                <div>
                  <Label className="text-gray-500">Açıklama</Label>
                  <p className="mt-1 whitespace-pre-wrap">{selectedJob.description}</p>
                </div>
              )}
              
              {/* İş ile Alakalı Belgeler */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <Label className="text-gray-700 flex items-center gap-2 mb-3">
                  <File className="h-4 w-4 text-amber-600" />
                  İş ile Alakalı Belgeler
                </Label>
                {jobDocumentsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600"></div>
                    <span className="ml-2 text-sm text-gray-500">Belgeler yükleniyor...</span>
                  </div>
                ) : jobDocuments.length > 0 ? (
                  <div className="space-y-2">
                    {jobDocuments.map((doc, index) => (
                      <div 
                        key={doc.id || index} 
                        className="flex items-center justify-between bg-white p-3 rounded-md border border-gray-200 hover:border-amber-300 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-amber-100 rounded">
                            <FileIcon className="h-5 w-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{doc.original_filename || doc.filename}</p>
                            <p className="text-xs text-gray-400">
                              {doc.created_at ? new Date(doc.created_at).toLocaleDateString('tr-TR') : ''}
                              {doc.file_size && ` • ${(doc.file_size / 1024).toFixed(1)} KB`}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadJobDocument(doc)}
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          data-testid={`download-doc-${doc.id}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          İndir
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-400 py-4">Bu işe ait belge bulunmuyor</p>
                )}
              </div>
              
              <div className="pt-2 border-t">
                <p className="text-xs text-gray-400">
                  Oluşturan: {selectedJob.created_by_name} | 
                  Oluşturma: {selectedJob.created_at ? new Date(selectedJob.created_at).toLocaleString('tr-TR') : '-'}
                </p>
              </div>
              
              {/* İletme Bilgisi - İletilmiş işler için göster */}
              {selectedJob.forwarded_to && (
                <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <Label className="text-indigo-700 flex items-center gap-2 mb-2">
                    <Send className="h-4 w-4" />
                    İletme Bilgisi
                  </Label>
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-gray-500">İletilen Panel:</span>{' '}
                      <Badge className="bg-indigo-100 text-indigo-700">
                        {selectedJob.forwarded_to === 'purchasing' ? 'Satın Alma' : 
                         selectedJob.forwarded_to === 'hr' ? 'İK' :
                         selectedJob.forwarded_to === 'accounting' ? 'Muhasebe' :
                         selectedJob.forwarded_to === 'admin' ? 'İdari İşler' : selectedJob.forwarded_to}
                      </Badge>
                    </p>
                    <p><span className="text-gray-500">İleten:</span> {selectedJob.forwarded_by_name || '-'}</p>
                    <p><span className="text-gray-500">İletme Tarihi:</span> {selectedJob.forwarded_at ? new Date(selectedJob.forwarded_at).toLocaleString('tr-TR') : '-'}</p>
                    {selectedJob.forwarded_note && (
                      <div>
                        <span className="text-gray-500">İletme Notu:</span>
                        <p className="mt-1 text-indigo-700 bg-white p-2 rounded border border-indigo-200">{selectedJob.forwarded_note}</p>
                      </div>
                    )}
                    {selectedJob.forwarded_to_job_id && (
                      <p className="text-xs text-indigo-500">
                        İletilen İş ID: {selectedJob.forwarded_to_job_id}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Arşiv Bilgisi */}
              {selectedJob.is_archived && (
                <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <Label className="text-orange-700 flex items-center gap-2 mb-2">
                    <Archive className="h-4 w-4" />
                    Arşiv Bilgisi
                  </Label>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Arşivleyen:</span> {selectedJob.archived_by_name || '-'}</p>
                    <p><span className="text-gray-500">Arşivlenme Tarihi:</span> {selectedJob.archived_at ? new Date(selectedJob.archived_at).toLocaleString('tr-TR') : '-'}</p>
                    {selectedJob.archive_reason && (
                      <div>
                        <span className="text-gray-500">Arşiv Açıklaması:</span>
                        <p className="mt-1 text-gray-700 bg-white p-2 rounded border">{selectedJob.archive_reason}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setDetailModalOpen(false)}>Kapat</Button>
            
            {/* İşi Tamamla butonu - aktif işlerde görünür */}
            {selectedJob?.status === 'active' && (
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => openCompleteModal(selectedJob)}
                data-testid="complete-job-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                İşi Tamamla
              </Button>
            )}
            
            {/* İşi İptal Et butonu - şube yöneticileri için tüm aktif işlerde görünür */}
            {selectedJob?.status === 'active' && (
              <Button 
                variant="outline"
                className="border-red-500 text-red-600 hover:bg-red-50"
                onClick={() => openCancelModal(selectedJob)}
                data-testid="cancel-job-btn"
              >
                <Ban className="h-4 w-4 mr-2" />
                İşi İptal Et
              </Button>
            )}
            
            {/* Ara Ver butonu - şube yöneticileri için tüm aktif işlerde görünür */}
            {selectedJob?.status === 'active' && (
              <Button 
                variant="outline"
                className="border-amber-500 text-amber-600 hover:bg-amber-50"
                onClick={() => handleQuickStatusUpdate(selectedJob, 'paused')}
                data-testid="pause-job-btn"
              >
                <Pause className="h-4 w-4 mr-2" />
                Ara Ver
              </Button>
            )}
            
            {/* Ara verilmiş işteyken: Aktif Et butonu */}
            {selectedJob?.status === 'paused' && (
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => handleQuickStatusUpdate(selectedJob, 'active')}
                data-testid="activate-job-btn"
              >
                <Play className="h-4 w-4 mr-2" />
                İşi Aktif Et
              </Button>
            )}
            
            {/* İptal edilmiş işteyken: Aktif Et butonu */}
            {selectedJob?.status === 'cancelled' && (
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => handleQuickStatusUpdate(selectedJob, 'active')}
                data-testid="reactivate-job-btn"
              >
                <Play className="h-4 w-4 mr-2" />
                İşi Aktif Et
              </Button>
            )}
            
            {/* Düzenle butonu - şube yöneticileri için tüm işlerde görünür */}
            <Button 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => {
                setDetailModalOpen(false);
                openEditModal(selectedJob);
              }}
            >
              <Edit className="h-4 w-4 mr-2" />
              Düzenle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Progress Modal */}
      <Dialog open={progressModalOpen} onOpenChange={setProgressModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-amber-600" />
              İş İlerleme Seviyesi
            </DialogTitle>
            <DialogDescription>
              {selectedJob?.name} işi için ilerleme seviyesini girin
            </DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <div className="text-center mb-6">
              <span className="text-5xl font-bold text-amber-600">{progressValue}%</span>
            </div>
            <Slider
              value={[progressValue]}
              onValueChange={(vals) => setProgressValue(vals[0])}
              max={100}
              step={5}
              className="mb-4"
            />
            <div className="flex justify-between text-sm text-gray-500">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProgressModalOpen(false)}>İptal</Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleUpdateProgress}
              data-testid="save-progress-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Durum Güncelleme Modal */}
      <Dialog open={statusUpdateModalOpen} onOpenChange={setStatusUpdateModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-600" />
              Durum Güncelle
            </DialogTitle>
            <DialogDescription>
              {statusUpdateJob?.name} işinin durumunu güncelleyin
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Durum</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="status-select">
                  <SelectValue placeholder="Durum seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      Devam Ediyor
                    </div>
                  </SelectItem>
                  <SelectItem value="paused">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                      Ara Verildi
                    </div>
                  </SelectItem>
                  <SelectItem value="completed">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      Tamamlandı
                    </div>
                  </SelectItem>
                  <SelectItem value="cancelled">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      İptal Edildi
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Açıklama (Opsiyonel)</Label>
              <Textarea
                placeholder="Durum değişikliği hakkında açıklama yazın..."
                value={statusDescription}
                onChange={(e) => setStatusDescription(e.target.value)}
                rows={3}
                data-testid="status-description"
              />
            </div>
            
            {/* Mevcut durum bilgisi */}
            {statusUpdateJob && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Mevcut Durum:</span>{' '}
                  {statusUpdateJob.status === 'active' ? 'Aktif' : 
                   statusUpdateJob.status === 'in_progress' ? 'Devam Ediyor' :
                   statusUpdateJob.status === 'completed' ? 'Tamamlandı' :
                   statusUpdateJob.status === 'cancelled' ? 'İptal Edildi' : 
                   statusUpdateJob.status || 'Aktif'}
                </p>
                {statusUpdateJob.status_description && (
                  <p className="text-sm text-gray-500 mt-1">
                    <span className="font-medium">Son Açıklama:</span> {statusUpdateJob.status_description}
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusUpdateModalOpen(false)}>
              İptal
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleStatusUpdate}
              disabled={updatingStatus || !newStatus}
              data-testid="save-status-btn"
            >
              {updatingStatus ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Modal */}
      <Dialog open={archiveModalOpen} onOpenChange={setArchiveModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-orange-600 flex items-center gap-2">
              <Archive className="h-5 w-5" />
              İşi Arşive Gönder
            </DialogTitle>
            <DialogDescription>
              "{selectedJob?.name}" işini arşive göndermek istediğinize emin misiniz? 
              İş tüm raporlar, iş programı ve görevleriyle birlikte arşive taşınacak.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveModalOpen(false)}>İptal</Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-700"
              onClick={handleArchiveJob}
              data-testid="confirm-archive-job-btn"
            >
              <Archive className="h-4 w-4 mr-2" />
              Arşive Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raporlar Modal */}
      <Dialog open={reportsModalOpen} onOpenChange={setReportsModalOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" />
              {selectedJob?.name} - Raporlar
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {itemLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-purple-600" /></div>
            ) : jobReports.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Bu işe ait rapor yok</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Başlık</TableHead>
                    <TableHead>Tür</TableHead>
                    <TableHead>Oluşturan</TableHead>
                    <TableHead>Tarih</TableHead>
                    <TableHead>İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobReports.map(report => (
                    <TableRow key={report.id} className="cursor-pointer hover:bg-gray-50" onClick={() => viewReportDetail(report)}>
                      <TableCell className="font-medium">{report.title}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{report.report_type === 'general' ? 'Genel' : report.report_type}</Badge>
                      </TableCell>
                      <TableCell>{report.created_by_name}</TableCell>
                      <TableCell>{report.created_at ? new Date(report.created_at).toLocaleDateString('tr-TR') : '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); viewReportDetail(report); }} title="Detay">
                            <Eye className="h-4 w-4 text-blue-600" />
                          </Button>
                          {!isJobReadOnly && (
                            <Button size="sm" variant="outline" className="text-red-600" onClick={(e) => { e.stopPropagation(); handleDeleteReport(report.id); }} title="Sil">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            
            {/* Rapor Ekleme Formu - Salt okunur modda gizle */}
            {!isJobReadOnly && (
              addReportOpen ? (
              <div className="mt-4 p-4 border rounded-lg bg-gray-50 space-y-3">
                <h4 className="font-medium flex items-center gap-2"><Plus className="h-4 w-4" />Yeni Rapor Ekle</h4>
                <div className="space-y-2">
                  <Label>Başlık *</Label>
                  <Input 
                    value={newReport.title} 
                    onChange={(e) => setNewReport(prev => ({...prev, title: e.target.value}))}
                    placeholder="Rapor başlığı"
                    data-testid="new-report-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>İçerik</Label>
                  <Textarea 
                    value={newReport.content} 
                    onChange={(e) => setNewReport(prev => ({...prev, content: e.target.value}))}
                    placeholder="Rapor içeriği"
                    rows={3}
                  />
                </div>
                {/* Belge Yükleme */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Upload className="h-4 w-4" />Belgeler</Label>
                  <DocumentUploader inputId="report-file-upload" />
                  <SelectedFilesList />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setAddReportOpen(false); setSelectedFiles([]); }}>İptal</Button>
                  <Button onClick={handleAddReport} className="bg-purple-600 hover:bg-purple-700" data-testid="submit-new-report" disabled={uploadingFiles}>
                    {uploadingFiles ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                    {uploadingFiles ? 'Yükleniyor...' : 'Ekle'}
                  </Button>
                </div>
              </div>
            ) : (
              <Button 
                className="mt-4 w-full bg-purple-600 hover:bg-purple-700" 
                onClick={() => setAddReportOpen(true)}
                data-testid="add-report-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Yeni Rapor Ekle
              </Button>
            )
            )}
            
            {/* Salt okunur mod bildirimi */}
            {isJobReadOnly && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Bu iş {selectedJob?.status === 'paused' ? 'ara verilmiş' : 'iptal edilmiş'} durumda olduğu için yeni rapor eklenemez.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* İş Programı Modal */}
      <Dialog open={schedulesModalOpen} onOpenChange={setSchedulesModalOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-blue-600" />
              {selectedJob?.name} - İş Programı
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {/* Tab Seçimi */}
            <div className="flex gap-2 mb-4 border-b">
              <button
                className={`px-4 py-2 font-medium text-sm transition-colors ${schedulesTab === 'active' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setSchedulesTab('active')}
              >
                Aktif ({jobSchedules.length})
              </button>
              <button
                className={`px-4 py-2 font-medium text-sm transition-colors ${schedulesTab === 'archive' ? 'border-b-2 border-orange-600 text-orange-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setSchedulesTab('archive')}
              >
                <Archive className="h-4 w-4 inline mr-1" />
                Arşiv ({archivedSchedules.length})
              </button>
            </div>

            {itemLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
            ) : schedulesTab === 'active' ? (
              jobSchedules.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CalendarDays className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Bu işe ait iş programı yok</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Başlık</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Saat</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>Oluşturan</TableHead>
                      <TableHead>İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jobSchedules.map(schedule => (
                      <TableRow key={schedule.id} className="cursor-pointer hover:bg-gray-50" onClick={() => viewScheduleDetail(schedule)}>
                        <TableCell className="font-medium">{schedule.title}</TableCell>
                        <TableCell>{schedule.scheduled_date}</TableCell>
                        <TableCell>{schedule.scheduled_time || '-'}</TableCell>
                        <TableCell>
                          <Badge className={schedule.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {schedule.status === 'completed' ? 'Tamamlandı' : 'Bekliyor'}
                          </Badge>
                        </TableCell>
                        <TableCell>{schedule.created_by_name}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); viewScheduleDetail(schedule); }} title="Detay">
                              <Eye className="h-4 w-4 text-blue-600" />
                            </Button>
                            {!isJobReadOnly && (
                              <Button size="sm" variant="outline" className="text-orange-600" onClick={(e) => { e.stopPropagation(); handleArchiveSchedule(schedule.id); }} title="Arşivle">
                                <Archive className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
            ) : (
              archivedSchedules.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Archive className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Arşivlenmiş iş programı yok</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Başlık</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Saat</TableHead>
                      <TableHead>Arşivleyen</TableHead>
                      <TableHead>Arşivlenme Tarihi</TableHead>
                      <TableHead>İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedSchedules.map(schedule => (
                      <TableRow key={schedule.id} className="cursor-pointer hover:bg-gray-50 bg-gray-50/50" onClick={() => viewScheduleDetail(schedule)}>
                        <TableCell className="font-medium text-gray-600">{schedule.title}</TableCell>
                        <TableCell className="text-gray-500">{schedule.scheduled_date}</TableCell>
                        <TableCell className="text-gray-500">{schedule.scheduled_time || '-'}</TableCell>
                        <TableCell className="text-gray-500">{schedule.archived_by_name || '-'}</TableCell>
                        <TableCell className="text-gray-500">{schedule.archived_at ? new Date(schedule.archived_at).toLocaleDateString('tr-TR') : '-'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); viewScheduleDetail(schedule); }} title="Detay">
                              <Eye className="h-4 w-4 text-blue-600" />
                            </Button>
                            {!isJobReadOnly && (
                              <Button size="sm" variant="outline" className="text-green-600" onClick={(e) => { e.stopPropagation(); handleRestoreSchedule(schedule.id); }} title="Arşivden Çıkar">
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
            )}
            
            {/* İş Programı Ekleme Formu - Salt okunur modda gizle */}
            {!isJobReadOnly && (
              addScheduleOpen ? (
              <div className="mt-4 p-4 border rounded-lg bg-gray-50 space-y-3">
                <h4 className="font-medium flex items-center gap-2"><Plus className="h-4 w-4" />Yeni İş Programı Ekle</h4>
                <div className="space-y-2">
                  <Label>Başlık *</Label>
                  <Input 
                    value={newSchedule.title} 
                    onChange={(e) => setNewSchedule(prev => ({...prev, title: e.target.value}))}
                    placeholder="İş programı başlığı"
                    data-testid="new-schedule-title"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Tarih *</Label>
                    <Input 
                      type="date"
                      value={newSchedule.scheduled_date} 
                      onChange={(e) => setNewSchedule(prev => ({...prev, scheduled_date: e.target.value}))}
                      data-testid="new-schedule-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Saat</Label>
                    <Input 
                      type="time"
                      value={newSchedule.scheduled_time} 
                      onChange={(e) => setNewSchedule(prev => ({...prev, scheduled_time: e.target.value}))}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Açıklama</Label>
                  <Textarea 
                    value={newSchedule.description} 
                    onChange={(e) => setNewSchedule(prev => ({...prev, description: e.target.value}))}
                    placeholder="Açıklama"
                    rows={2}
                  />
                </div>
                {/* Belge Yükleme */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Upload className="h-4 w-4" />Belgeler</Label>
                  <DocumentUploader inputId="schedule-file-upload" />
                  <SelectedFilesList />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setAddScheduleOpen(false); setSelectedFiles([]); }}>İptal</Button>
                  <Button onClick={handleAddSchedule} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-new-schedule" disabled={uploadingFiles}>
                    {uploadingFiles ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                    {uploadingFiles ? 'Yükleniyor...' : 'Ekle'}
                  </Button>
                </div>
              </div>
            ) : (
              <Button 
                className="mt-4 w-full bg-blue-600 hover:bg-blue-700" 
                onClick={() => setAddScheduleOpen(true)}
                data-testid="add-schedule-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Yeni İş Programı Ekle
              </Button>
            )
            )}
            
            {/* Salt okunur mod bildirimi */}
            {isJobReadOnly && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Bu iş {selectedJob?.status === 'paused' ? 'ara verilmiş' : 'iptal edilmiş'} durumda olduğu için yeni iş programı eklenemez.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Görevler Modal */}
      <Dialog open={tasksModalOpen} onOpenChange={setTasksModalOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-green-600" />
              {selectedJob?.name} - Görevler
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {/* Tab Seçimi */}
            <div className="flex gap-2 mb-4 border-b">
              <button
                className={`px-4 py-2 font-medium text-sm transition-colors ${tasksTab === 'active' ? 'border-b-2 border-green-600 text-green-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setTasksTab('active')}
              >
                Aktif ({jobTasks.length})
              </button>
              <button
                className={`px-4 py-2 font-medium text-sm transition-colors ${tasksTab === 'archive' ? 'border-b-2 border-orange-600 text-orange-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setTasksTab('archive')}
              >
                <Archive className="h-4 w-4 inline mr-1" />
                Arşiv ({archivedTasks.length})
              </button>
            </div>

            {itemLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-green-600" /></div>
            ) : tasksTab === 'active' ? (
              jobTasks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ClipboardList className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Bu işe ait görev yok</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Başlık</TableHead>
                      <TableHead>Atanan</TableHead>
                      <TableHead>Bitiş Tarihi</TableHead>
                      <TableHead>Öncelik</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jobTasks.map(task => (
                      <TableRow key={task.id} className="cursor-pointer hover:bg-gray-50" onClick={() => viewTaskDetail(task)}>
                        <TableCell className="font-medium">{task.title}</TableCell>
                        <TableCell>{task.assigned_to_name || '-'}</TableCell>
                        <TableCell>{task.due_date || '-'}</TableCell>
                        <TableCell>{getPriorityBadge(task.priority)}</TableCell>
                        <TableCell>{getTaskStatusBadge(task.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); viewTaskDetail(task); }} title="Detay">
                              <Eye className="h-4 w-4 text-blue-600" />
                            </Button>
                            {!isJobReadOnly && (
                              <Button size="sm" variant="outline" className="text-orange-600" onClick={(e) => { e.stopPropagation(); handleArchiveTask(task.id); }} title="Arşivle">
                                <Archive className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
            ) : (
              archivedTasks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Archive className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Arşivlenmiş görev yok</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Başlık</TableHead>
                      <TableHead>Atanan</TableHead>
                      <TableHead>Öncelik</TableHead>
                      <TableHead>Arşivleyen</TableHead>
                      <TableHead>Arşivlenme Tarihi</TableHead>
                      <TableHead>İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedTasks.map(task => (
                      <TableRow key={task.id} className="cursor-pointer hover:bg-gray-50 bg-gray-50/50" onClick={() => viewTaskDetail(task)}>
                        <TableCell className="font-medium text-gray-600">{task.title}</TableCell>
                        <TableCell className="text-gray-500">{task.assigned_to_name || '-'}</TableCell>
                        <TableCell>{getPriorityBadge(task.priority)}</TableCell>
                        <TableCell className="text-gray-500">{task.archived_by_name || '-'}</TableCell>
                        <TableCell className="text-gray-500">{task.archived_at ? new Date(task.archived_at).toLocaleDateString('tr-TR') : '-'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); viewTaskDetail(task); }} title="Detay">
                              <Eye className="h-4 w-4 text-blue-600" />
                            </Button>
                            {!isJobReadOnly && (
                              <Button size="sm" variant="outline" className="text-green-600" onClick={(e) => { e.stopPropagation(); handleRestoreTask(task.id); }} title="Arşivden Çıkar">
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
            )}
            
            {/* Görev Ekleme Formu - Salt okunur modda gizle */}
            {!isJobReadOnly && (
              addTaskOpen ? (
              <div className="mt-4 p-4 border rounded-lg bg-gray-50 space-y-3">
                <h4 className="font-medium flex items-center gap-2"><Plus className="h-4 w-4" />Yeni Görev Ekle</h4>
                <div className="space-y-2">
                  <Label>Başlık *</Label>
                  <Input 
                    value={newTask.title} 
                    onChange={(e) => setNewTask(prev => ({...prev, title: e.target.value}))}
                    placeholder="Görev başlığı"
                    data-testid="new-task-title"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Atanan Kişi</Label>
                    <Select value={newTask.assigned_to} onValueChange={(value) => setNewTask(prev => ({...prev, assigned_to: value}))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Kişi seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {branchUsers.map(user => (
                          <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Bitiş Tarihi</Label>
                    <Input 
                      type="date"
                      value={newTask.due_date} 
                      onChange={(e) => setNewTask(prev => ({...prev, due_date: e.target.value}))}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Öncelik</Label>
                  <Select value={newTask.priority} onValueChange={(value) => setNewTask(prev => ({...prev, priority: value}))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Öncelik seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Düşük</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">Yüksek</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Açıklama</Label>
                  <Textarea 
                    value={newTask.description} 
                    onChange={(e) => setNewTask(prev => ({...prev, description: e.target.value}))}
                    placeholder="Görev açıklaması"
                    rows={2}
                  />
                </div>
                {/* Belge Yükleme */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Upload className="h-4 w-4" />Belgeler</Label>
                  <DocumentUploader inputId="task-file-upload" />
                  <SelectedFilesList />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setAddTaskOpen(false); setSelectedFiles([]); }}>İptal</Button>
                  <Button onClick={handleAddTask} className="bg-green-600 hover:bg-green-700" data-testid="submit-new-task" disabled={uploadingFiles}>
                    {uploadingFiles ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                    {uploadingFiles ? 'Yükleniyor...' : 'Ekle'}
                  </Button>
                </div>
              </div>
            ) : (
              <Button 
                className="mt-4 w-full bg-green-600 hover:bg-green-700" 
                onClick={() => setAddTaskOpen(true)}
                data-testid="add-task-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Yeni Görev Ekle
              </Button>
            )
            )}
            
            {/* Salt okunur mod bildirimi */}
            {isJobReadOnly && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Bu iş {selectedJob?.status === 'paused' ? 'ara verilmiş' : 'iptal edilmiş'} durumda olduğu için yeni görev eklenemez.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Belge Önizleme Modal */}
      <Dialog open={previewModalOpen} onOpenChange={setPreviewModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Belge Önizleme: {previewDoc?.original_filename}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {previewDoc && (
              previewDoc.file_extension === '.pdf' ? (
                <iframe 
                  src={`${API}/documents/${previewDoc.id}/preview`}
                  className="w-full h-[70vh] border rounded"
                  title="PDF Preview"
                />
              ) : (
                <img 
                  src={`${API}/documents/${previewDoc.id}/preview`}
                  alt={previewDoc.original_filename}
                  className="max-w-full max-h-[70vh] mx-auto"
                />
              )
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleDownloadDocument(previewDoc)}>
              <Download className="h-4 w-4 mr-2" />
              İndir
            </Button>
            <Button onClick={() => setPreviewModalOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rapor Detay Modal */}
      <Dialog open={reportDetailModal} onOpenChange={setReportDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" />
              Rapor Detayı
            </DialogTitle>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-gray-500">Başlık</Label>
                <p className="font-medium text-lg">{selectedReport.title}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Rapor Türü</Label>
                  <p className="font-medium">{selectedReport.report_type === 'general' ? 'Genel' : selectedReport.report_type}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Oluşturma Tarihi</Label>
                  <p className="font-medium">{selectedReport.created_at ? new Date(selectedReport.created_at).toLocaleString('tr-TR') : '-'}</p>
                </div>
              </div>
              <div>
                <Label className="text-gray-500">Oluşturan</Label>
                <p className="font-medium">{selectedReport.created_by_name}</p>
              </div>
              {selectedReport.content && (
                <div>
                  <Label className="text-gray-500">İçerik</Label>
                  <p className="mt-1 whitespace-pre-wrap bg-gray-50 p-3 rounded">{selectedReport.content}</p>
                </div>
              )}
              <DocumentsList documents={itemDocuments} onRefresh={() => viewReportDetail(selectedReport)} readOnly={isJobReadOnly} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportDetailModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İş Programı Detay Modal */}
      <Dialog open={scheduleDetailModal} onOpenChange={setScheduleDetailModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-blue-600" />
              İş Programı Detayı
            </DialogTitle>
          </DialogHeader>
          {selectedSchedule && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-gray-500">Başlık</Label>
                <p className="font-medium text-lg">{selectedSchedule.title}</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-500">Tarih</Label>
                  <p className="font-medium">{selectedSchedule.scheduled_date}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Saat</Label>
                  <p className="font-medium">{selectedSchedule.scheduled_time || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Durum</Label>
                  <Badge className={selectedSchedule.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                    {selectedSchedule.status === 'completed' ? 'Tamamlandı' : 'Bekliyor'}
                  </Badge>
                </div>
              </div>
              <div>
                <Label className="text-gray-500">Oluşturan</Label>
                <p className="font-medium">{selectedSchedule.created_by_name}</p>
              </div>
              {selectedSchedule.completed_at && (
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <Label className="text-green-700">Tamamlanma Bilgisi</Label>
                  <p className="text-sm text-green-600 mt-1">
                    {selectedSchedule.completed_by_name} tarafından {new Date(selectedSchedule.completed_at).toLocaleString('tr-TR')} tarihinde tamamlandı
                  </p>
                </div>
              )}
              {selectedSchedule.description && (
                <div>
                  <Label className="text-gray-500">Açıklama</Label>
                  <p className="mt-1 whitespace-pre-wrap bg-gray-50 p-3 rounded">{selectedSchedule.description}</p>
                </div>
              )}
              
              {/* Güncellemeler Geçmişi */}
              {selectedSchedule.updates && selectedSchedule.updates.length > 0 && (
                <div>
                  <Label className="text-gray-500 flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Güncellemeler ({selectedSchedule.updates.length})
                  </Label>
                  <div className="mt-2 space-y-3 max-h-60 overflow-y-auto">
                    {selectedSchedule.updates.map((update, idx) => (
                      <div key={update.id || idx} className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-medium text-blue-800 text-sm">{update.created_by_name}</span>
                          <span className="text-xs text-blue-600">
                            {new Date(update.created_at).toLocaleString('tr-TR')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{update.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <DocumentsList documents={itemDocuments} onRefresh={() => viewScheduleDetail(selectedSchedule)} readOnly={isJobReadOnly} />
              
              {/* İşlem Butonları - Salt okunur modda gizle */}
              {selectedSchedule.status !== 'completed' && !isJobReadOnly && (
                <div className="flex gap-2 pt-4 border-t">
                  <Button 
                    onClick={openScheduleUpdateModal}
                    variant="outline"
                    className="flex-1"
                    data-testid="schedule-update-btn"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Güncelle
                  </Button>
                  <Button 
                    onClick={handleCompleteSchedule}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={completingSchedule}
                    data-testid="schedule-complete-btn"
                  >
                    {completingSchedule ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    Tamamla
                  </Button>
                </div>
              )}
              
              {/* Salt okunur mod bildirimi */}
              {isJobReadOnly && selectedSchedule.status !== 'completed' && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Bu iş {selectedJob?.status === 'paused' ? 'ara verilmiş' : 'iptal edilmiş'} durumda olduğu için iş programı güncellenemez.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDetailModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İş Programı Güncelleme Modal */}
      <Dialog open={scheduleUpdateModal} onOpenChange={setScheduleUpdateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              İş Programı Güncellemesi
            </DialogTitle>
            <DialogDescription>
              Yaptığınız işleri yazın ve gerekirse belge ekleyin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Yapılan İşler *</Label>
              <Textarea
                value={scheduleUpdateText}
                onChange={(e) => setScheduleUpdateText(e.target.value)}
                placeholder="İş programı ile ilgili yaptığınız işleri detaylı açıklayın..."
                rows={4}
                data-testid="schedule-update-description"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Belgeler (Opsiyonel)
              </Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={handleScheduleUpdateFileSelect}
                  className="hidden"
                  id="schedule-update-file-upload"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg,.gif,.zip,.rar"
                />
                <label htmlFor="schedule-update-file-upload" className="cursor-pointer">
                  <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">Belge eklemek için tıklayın</p>
                  <p className="text-xs text-gray-400 mt-1">PDF, Word, Excel, Resim (max 10MB)</p>
                </label>
              </div>
              
              {scheduleUpdateFiles.length > 0 && (
                <div className="space-y-2 mt-2">
                  {scheduleUpdateFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                        <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeScheduleUpdateFile(index)}
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleUpdateModal(false)}>İptal</Button>
            <Button 
              onClick={handleSubmitScheduleUpdate}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={submittingScheduleUpdate || !scheduleUpdateText.trim()}
              data-testid="submit-schedule-update"
            >
              {submittingScheduleUpdate ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Güncellemeyi Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Görev Detay Modal */}
      <Dialog open={taskDetailModal} onOpenChange={setTaskDetailModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-green-600" />
              Görev Detayı
            </DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-gray-500">Başlık</Label>
                <p className="font-medium text-lg">{selectedTask.title}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Atanan Kişi</Label>
                  <p className="font-medium">{selectedTask.assigned_to_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Bitiş Tarihi</Label>
                  <p className="font-medium">{selectedTask.due_date || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Öncelik</Label>
                  <div className="mt-1">{getPriorityBadge(selectedTask.priority)}</div>
                </div>
                <div>
                  <Label className="text-gray-500">Durum</Label>
                  <div className="mt-1">{getTaskStatusBadge(selectedTask.status)}</div>
                </div>
              </div>
              <div>
                <Label className="text-gray-500">Oluşturan</Label>
                <p className="font-medium">{selectedTask.created_by_name}</p>
              </div>
              {selectedTask.completed_at && (
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <Label className="text-green-700">Tamamlanma Bilgisi</Label>
                  <p className="text-sm text-green-600 mt-1">
                    {selectedTask.completed_by_name} tarafından {new Date(selectedTask.completed_at).toLocaleString('tr-TR')} tarihinde tamamlandı
                  </p>
                </div>
              )}
              {selectedTask.description && (
                <div>
                  <Label className="text-gray-500">Açıklama</Label>
                  <p className="mt-1 whitespace-pre-wrap bg-gray-50 p-3 rounded">{selectedTask.description}</p>
                </div>
              )}
              
              {/* Güncellemeler Geçmişi */}
              {selectedTask.updates && selectedTask.updates.length > 0 && (
                <div>
                  <Label className="text-gray-500 flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Güncellemeler ({selectedTask.updates.length})
                  </Label>
                  <div className="mt-2 space-y-3 max-h-60 overflow-y-auto">
                    {selectedTask.updates.map((update, idx) => (
                      <div key={update.id || idx} className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-medium text-blue-800 text-sm">{update.created_by_name}</span>
                          <span className="text-xs text-blue-600">
                            {new Date(update.created_at).toLocaleString('tr-TR')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{update.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <DocumentsList documents={itemDocuments} onRefresh={() => viewTaskDetail(selectedTask)} readOnly={isJobReadOnly} />
              
              {/* İşlem Butonları - Salt okunur modda gizle */}
              {selectedTask.status !== 'completed' && !isJobReadOnly && (
                <div className="flex gap-2 pt-4 border-t">
                  <Button 
                    onClick={openTaskUpdateModal}
                    variant="outline"
                    className="flex-1"
                    data-testid="task-update-btn"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Güncelle
                  </Button>
                  <Button 
                    onClick={handleCompleteTask}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={completingTask}
                    data-testid="task-complete-btn"
                  >
                    {completingTask ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    Görevi Bitir
                  </Button>
                </div>
              )}
              
              {/* Salt okunur mod bildirimi */}
              {isJobReadOnly && selectedTask.status !== 'completed' && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Bu iş {selectedJob?.status === 'paused' ? 'ara verilmiş' : 'iptal edilmiş'} durumda olduğu için görev güncellenemez.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskDetailModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Görev Güncelleme Modal */}
      <Dialog open={taskUpdateModal} onOpenChange={setTaskUpdateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Görev Güncellemesi
            </DialogTitle>
            <DialogDescription>
              Yaptığınız işleri yazın ve gerekirse belge ekleyin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Yapılan İşler *</Label>
              <Textarea
                value={taskUpdateText}
                onChange={(e) => setTaskUpdateText(e.target.value)}
                placeholder="Görevle ilgili yaptığınız işleri detaylı açıklayın..."
                rows={4}
                data-testid="task-update-description"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Belgeler (Opsiyonel)
              </Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={handleTaskUpdateFileSelect}
                  className="hidden"
                  id="task-update-file-upload"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg,.gif,.zip,.rar"
                />
                <label htmlFor="task-update-file-upload" className="cursor-pointer">
                  <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">Belge eklemek için tıklayın</p>
                  <p className="text-xs text-gray-400 mt-1">PDF, Word, Excel, Resim (max 10MB)</p>
                </label>
              </div>
              
              {taskUpdateFiles.length > 0 && (
                <div className="space-y-2 mt-2">
                  {taskUpdateFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                        <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTaskUpdateFile(index)}
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskUpdateModal(false)}>İptal</Button>
            <Button 
              onClick={handleSubmitTaskUpdate}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={submittingTaskUpdate || !taskUpdateText.trim()}
              data-testid="submit-task-update"
            >
              {submittingTaskUpdate ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Güncellemeyi Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Talep Ekle Modal */}
      <Dialog open={talepModalOpen} onOpenChange={setTalepModalOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <Send className="h-5 w-5" />
              Yeni Talep Oluştur
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kategori</Label>
                <Select 
                  value={talepFormData.category} 
                  onValueChange={(val) => setTalepFormData(prev => ({ ...prev, category: val, title: '' }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Kategori seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {talepCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Başlık * <span className="text-xs text-gray-500">(Belge yüklendiğinde otomatik dolar)</span></Label>
                <Input 
                  value={talepFormData.title} 
                  onChange={(e) => setTalepFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Belge yükleyin veya başlık yazın..."
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea
                value={talepFormData.description}
                onChange={(e) => setTalepFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Talep detaylarını yazın..."
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Öncelik</Label>
                <Select 
                  value={talepFormData.priority} 
                  onValueChange={(val) => setTalepFormData(prev => ({ ...prev, priority: val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Öncelik seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Düşük</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Yüksek</SelectItem>
                    <SelectItem value="urgent">Acil</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>İstenilen Tarih</Label>
                <Input
                  type="date"
                  value={talepFormData.expected_delivery_date}
                  onChange={(e) => setTalepFormData(prev => ({ ...prev, expected_delivery_date: e.target.value }))}
                />
              </div>
            </div>
            
            {/* Belgeler */}
            <div className="space-y-2">
              <Label className="text-red-600">Belgeler (Zorunlu) *</Label>
              {talepFormData.documents.length > 0 && (
                <div className="space-y-2 mb-2">
                  {talepFormData.documents.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-blue-600" />
                        <span className="text-sm truncate max-w-[200px]">{doc.name}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeTalepFile(idx)}>
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <Upload className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600">Belge Yükle</span>
                <input
                  type="file"
                  className="hidden"
                  multiple
                  onChange={handleTalepFileUpload}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                />
              </label>
              <p className="text-xs text-gray-500">İlk yüklenen belgenin adı otomatik başlık olarak kullanılır</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTalepModalOpen(false)}>İptal</Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleCreateTalep}
              disabled={talepSubmitting || !talepFormData.title || talepFormData.documents.length === 0}
            >
              {talepSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Talebi Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Talep Detay Modal */}
      <Dialog open={!!selectedTalep} onOpenChange={() => setSelectedTalep(null)}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <Eye className="h-5 w-5" />
              Talep Detayı
            </DialogTitle>
          </DialogHeader>
          {selectedTalep && (
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {/* Talep No */}
              {selectedTalep.request_number && (
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-purple-600" />
                    <span className="text-sm text-purple-700 font-medium">Talep No:</span>
                    <span className="text-sm text-purple-900 font-mono">{selectedTalep.request_number}</span>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-xs">Başlık</Label>
                  <p className="font-medium">{selectedTalep.title}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Kategori</Label>
                  <p className="font-medium">{talepCategories.find(c => c.id === selectedTalep.category)?.label || selectedTalep.category}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Tarih</Label>
                  <p className="font-medium">
                    {new Date(selectedTalep.created_at).toLocaleDateString('tr-TR')}
                    <span className="text-gray-500 text-sm ml-2">
                      {new Date(selectedTalep.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Durum</Label>
                  <Badge className={
                    selectedTalep.status === 'approved' ? 'bg-green-100 text-green-700' :
                    selectedTalep.status?.includes('rejected') ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }>
                    {selectedTalep.status === 'approved' ? 'Onaylandı' :
                     selectedTalep.status?.includes('rejected') ? 'Reddedildi' :
                     'Bekliyor'}
                  </Badge>
                </div>
              </div>
              
              {selectedTalep.description && (
                <div>
                  <Label className="text-gray-500 text-xs">Açıklama</Label>
                  <p className="text-sm mt-1 p-2 bg-gray-50 rounded">{selectedTalep.description}</p>
                </div>
              )}
              
              {selectedTalep.documents && selectedTalep.documents.length > 0 && (
                <div>
                  <Label className="text-gray-500 text-xs">Belgeler ({selectedTalep.documents.length})</Label>
                  <div className="space-y-2 mt-2">
                    {selectedTalep.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-2">
                          <File className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">{doc.name}</span>
                        </div>
                        {(doc.url || doc.data) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const fileSrc = doc.url || doc.data;
                              if (fileSrc.startsWith('data:')) {
                                const link = document.createElement('a');
                                link.href = fileSrc;
                                link.download = doc.name;
                                link.click();
                              } else {
                                window.open(fileSrc, '_blank');
                              }
                            }}
                            className="text-blue-600"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Yanıt mesajı (varsa) */}
              {selectedTalep.response_message && (
                <div className="p-3 bg-gray-100 rounded-lg">
                  <Label className="text-gray-500 text-xs">Yanıt</Label>
                  <p className="text-sm mt-1">{selectedTalep.response_message}</p>
                </div>
              )}

              {/* Red Nedeni (reddedilmiş talepler için) */}
              {selectedTalep.status?.includes('rejected') && (selectedTalep.rejection_reason || selectedTalep.response_message) && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center gap-2 mb-1">
                    <XCircle className="h-4 w-4 text-red-600" />
                    <Label className="text-red-700 font-medium text-sm">Red Nedeni</Label>
                  </div>
                  <p className="text-sm text-red-800">{selectedTalep.rejection_reason || selectedTalep.response_message}</p>
                </div>
              )}

              {/* Gönderen bilgisi (gelen talepler için) */}
              {selectedTalep.owner_name && taleplerSubTab === 'gelen' && (
                <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-indigo-600" />
                    <span className="text-sm text-indigo-700 font-medium">Gönderen:</span>
                    <span className="text-sm text-indigo-900">{selectedTalep.owner_name}</span>
                  </div>
                </div>
              )}
              
              {/* İşlem butonları (sadece bekleyen talepler için) */}
              {selectedTalep.status === 'pending' && (
                <div className="border-t pt-4 mt-4 space-y-4">
                  <Label className="text-gray-700 font-medium">İşlem Yap</Label>
                  
                  {/* Açıklama alanı */}
                  <div>
                    <Label className="text-gray-500 text-xs">Açıklama / Not</Label>
                    <Textarea
                      value={talepActionNote}
                      onChange={(e) => setTalepActionNote(e.target.value)}
                      placeholder="İşlem ile ilgili notunuzu yazın..."
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                  
                  {/* İşlem butonları */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => handleTalepAction('approve')}
                      disabled={talepActionLoading}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {talepActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                      Onayla
                    </Button>
                    <Button
                      onClick={() => handleTalepAction('approve_forward')}
                      disabled={talepActionLoading}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {talepActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      Onayla ve İlet
                    </Button>
                    <Button
                      onClick={() => handleTalepAction('reject')}
                      disabled={talepActionLoading}
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-50"
                    >
                      {talepActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                      Reddet
                    </Button>
                  </div>
                  
                  {/* Kategori bilgisi */}
                  <p className="text-xs text-gray-500">
                    "Onayla ve İlet" seçeneği talebi kategorisine göre ({talepCategories.find(c => c.id === selectedTalep.category)?.label || selectedTalep.category}) ilgili birime yönlendirir.
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelectedTalep(null); setTalepActionNote(''); }}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Arşiv Açıklaması Modal */}
      <Dialog open={archiveReasonModalOpen} onOpenChange={setArchiveReasonModalOpen}>
        <DialogContent className="max-w-md" data-testid="archive-reason-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Archive className="h-5 w-5" />
              Arşive Gönder
            </DialogTitle>
            <DialogDescription>
              Bu öğeyi arşive göndermek için bir açıklama yazmanız gerekmektedir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="archive-reason" className="text-sm font-medium">
                Arşiv Açıklaması <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="archive-reason"
                placeholder="Arşivleme nedenini yazın..."
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                className="min-h-[100px]"
                data-testid="archive-reason-input"
              />
              <p className="text-xs text-gray-500">
                Bu açıklama arşivlenen öğenin detaylarında görünecektir.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setArchiveReasonModalOpen(false);
                setArchiveReason('');
                setArchiveTarget({ type: null, id: null });
              }}
              disabled={archivingItem}
            >
              İptal
            </Button>
            <Button 
              onClick={handleConfirmArchive}
              className="bg-orange-600 hover:bg-orange-700"
              disabled={archivingItem || !archiveReason.trim()}
              data-testid="confirm-archive-btn"
            >
              {archivingItem ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Archive className="h-4 w-4 mr-2" />
              )}
              Arşive Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Genel Talepler için Kişi Seçim Modalı */}
      <Dialog open={showRecipientModal} onOpenChange={setShowRecipientModal}>
        <DialogContent className="max-w-md" data-testid="recipient-selection-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Users className="h-5 w-5" />
              Talebi Kime İletmek İstiyorsunuz?
            </DialogTitle>
            <DialogDescription>
              Bu genel talep için iletilecek kişiyi seçin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="recipient-select" className="text-sm font-medium">
                Alıcı Kişi <span className="text-red-500">*</span>
              </Label>
              <Select value={selectedRecipientId} onValueChange={setSelectedRecipientId}>
                <SelectTrigger id="recipient-select" data-testid="recipient-select">
                  <SelectValue placeholder="Kişi seçin..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {allUsers.length > 0 ? (
                    allUsers.map((user) => {
                      // Panel erişimini Türkçe'ye çevir
                      const getPanelLabel = (panelAccess) => {
                        if (!panelAccess || panelAccess.length === 0) return null;
                        const panelMap = {
                          'work': 'İş Paneli',
                          'branch': 'Şube Yönetimi',
                          'board': 'Yönetim Kurulu',
                          'hr': 'İK',
                          'accounting': 'Muhasebe',
                          'purchasing': 'Satın Alma',
                          'depo': 'Depo'
                        };
                        // İlk panel erişimini göster, birden fazla varsa hepsini göster
                        const panels = panelAccess.map(p => panelMap[p] || p).filter(Boolean);
                        return panels.length > 0 ? panels.join(', ') : null;
                      };
                      
                      const panelLabel = getPanelLabel(user.panel_access);
                      
                      return (
                        <SelectItem key={user.id} value={user.id}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{user.name}</span>
                            {user.branch_name && (
                              <Badge variant="secondary" className="text-xs">
                                {user.branch_name}
                              </Badge>
                            )}
                            {panelLabel && (
                              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                {panelLabel}
                              </Badge>
                            )}
                            {user.is_master_admin && (
                              <Badge className="text-xs bg-red-100 text-red-700 border-red-200">
                                Master Admin
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      );
                    })
                  ) : (
                    <SelectItem value="no-users" disabled>Kullanıcı bulunamadı</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Seçilen kişiye talep bildirim olarak iletilecektir.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowRecipientModal(false);
                setSelectedRecipientId('');
              }}
              disabled={talepActionLoading}
            >
              İptal
            </Button>
            <Button 
              onClick={() => handleTalepAction('approve_forward', selectedRecipientId)}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={talepActionLoading || !selectedRecipientId}
              data-testid="confirm-forward-btn"
            >
              {talepActionLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Onayla ve İlet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Red Nedeni Modalı */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="max-w-md" data-testid="reject-reason-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Talebi Reddet
            </DialogTitle>
            <DialogDescription>
              Lütfen red nedenini belirtin. Bu bilgi talep sahibine gösterilecektir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reject-reason" className="text-sm font-medium">
                Red Nedeni <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Red nedenini yazın..."
                rows={4}
                className="resize-none"
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowRejectModal(false);
                setRejectReason('');
              }}
            >
              İptal
            </Button>
            <Button 
              variant="destructive"
              onClick={handleRejectTalep}
              disabled={talepActionLoading || !rejectReason.trim()}
              data-testid="confirm-reject-btn"
            >
              {talepActionLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              Reddet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İş İletme Modalı */}
      <Dialog open={forwardModalOpen} onOpenChange={setForwardModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-indigo-600" />
              İşi İlet
            </DialogTitle>
            <DialogDescription>
              {forwardingJob?.name} işini başka bir panele iletin
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-700">Hedef Panel</Label>
              <Select value={forwardTargetPanel} onValueChange={setForwardTargetPanel}>
                <SelectTrigger className="mt-1" data-testid="forward-target-select">
                  <SelectValue placeholder="Panel seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="purchasing">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                      Satın Alma Paneli
                    </div>
                  </SelectItem>
                  <SelectItem value="hr">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      İK Paneli
                    </div>
                  </SelectItem>
                  <SelectItem value="accounting">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      Muhasebe Paneli
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                      İdari İşler Paneli
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-gray-700">Öncelik</Label>
              <Select value={forwardPriority} onValueChange={setForwardPriority}>
                <SelectTrigger className="mt-1" data-testid="forward-priority-select">
                  <SelectValue placeholder="Öncelik seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      Düşük
                    </div>
                  </SelectItem>
                  <SelectItem value="normal">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      Normal
                    </div>
                  </SelectItem>
                  <SelectItem value="high">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                      Yüksek
                    </div>
                  </SelectItem>
                  <SelectItem value="urgent">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      Acil
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-gray-700">Not (Opsiyonel)</Label>
              <Textarea
                placeholder="İletme ile ilgili not ekleyin..."
                value={forwardNote}
                onChange={(e) => setForwardNote(e.target.value)}
                rows={3}
                className="mt-1"
                data-testid="forward-note-textarea"
              />
            </div>
            
            {forwardingJob && (
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p className="font-medium text-gray-700 mb-1">İletilecek İş Bilgileri:</p>
                <p className="text-gray-600"><span className="font-medium">İş Adı:</span> {forwardingJob.name}</p>
                <p className="text-gray-600"><span className="font-medium">Şirket:</span> {forwardingJob.company}</p>
                <p className="text-gray-600"><span className="font-medium">Başlangıç:</span> {forwardingJob.start_date}</p>
                {forwardingJob.locations?.length > 0 && (
                  <p className="text-gray-600"><span className="font-medium">Konumlar:</span> {forwardingJob.locations.join(', ')}</p>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setForwardModalOpen(false)}
              disabled={isForwarding}
            >
              İptal
            </Button>
            <Button 
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={handleForwardJob}
              disabled={isForwarding}
              data-testid="confirm-forward-btn"
            >
              {isForwarding ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              İlet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============= İŞ DUVARI MODALLARI ============= */}
      
      {/* İş Duvarı Ana Modal */}
      <Dialog open={wallModalOpen} onOpenChange={setWallModalOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="job-wall-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <StickyNote className="h-5 w-5" />
              İş Duvarı - {selectedJob?.name}
            </DialogTitle>
            <DialogDescription>
              Bu iş ile ilgili paylaşımlar ve tartışmalar
            </DialogDescription>
          </DialogHeader>
          
          {/* Yeni Paylaşım Formu - Salt okunur modda gizle */}
          {!isJobReadOnly ? (
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <Label className="text-sm font-medium text-amber-800 mb-2 block">Yeni Paylaşım</Label>
            <Textarea
              value={wallPostContent}
              onChange={(e) => setWallPostContent(e.target.value)}
              placeholder="Paylaşmak istediğiniz mesajı yazın..."
              rows={3}
              className="mb-3"
              data-testid="wall-post-input"
            />
            
            {/* Belge yükleme */}
            <div className="mb-3">
              <input
                type="file"
                multiple
                onChange={(e) => handleWallFileUpload(e, false)}
                className="hidden"
                id="branch-wall-post-file"
              />
              <label 
                htmlFor="branch-wall-post-file" 
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md cursor-pointer hover:bg-gray-50"
              >
                <Paperclip className="h-4 w-4" />
                Belge Ekle
              </label>
              {wallPostDocuments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {wallPostDocuments.map((doc, idx) => (
                    <div key={idx} className="flex items-center gap-1 bg-white px-2 py-1 rounded text-sm border">
                      <FileText className="h-3 w-3" />
                      <span className="truncate max-w-[150px]">{doc.name}</span>
                      <button 
                        onClick={() => setWallPostDocuments(prev => prev.filter((_, i) => i !== idx))}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <Button 
              onClick={handleCreateWallPost} 
              disabled={wallSubmitting || !wallPostContent.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="create-wall-post-btn"
            >
              {wallSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Paylaş
            </Button>
          </div>
          ) : (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Bu iş {selectedJob?.status === 'paused' ? 'ara verilmiş' : 'iptal edilmiş'} durumda olduğu için yeni paylaşım yapılamaz ve içerikler düzenlenemez.
            </div>
          )}
          
          {/* Paylaşımlar Listesi */}
          <div className="mt-4 space-y-4">
            {wallLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
              </div>
            ) : wallPosts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <StickyNote className="h-12 w-12 mx-auto mb-3 opacity-40" />
                <p>Henüz paylaşım yok</p>
                <p className="text-sm">İlk paylaşımı siz yapın!</p>
              </div>
            ) : (
              wallPosts.map(post => (
                <div 
                  key={post.id} 
                  className={`p-4 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${!post.is_seen ? 'border-amber-400 bg-amber-50' : 'bg-white border-gray-200'}`}
                  onClick={() => openWallPostDetail(post)}
                  data-testid={`wall-post-${post.id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-semibold text-sm">
                        {post.created_by_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{post.created_by_name}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(post.created_at).toLocaleString('tr-TR')}
                        </p>
                      </div>
                    </div>
                    {!post.is_seen && (
                      <Badge className="bg-amber-600 text-white">YENİ</Badge>
                    )}
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{post.content}</p>
                  {post.documents?.length > 0 && (
                    <div className="mt-2 flex items-center gap-1 text-gray-500 text-sm">
                      <Paperclip className="h-3 w-3" />
                      <span>{post.documents.length} belge</span>
                    </div>
                  )}
                  <div className="mt-2 flex items-center gap-3 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <MessageSquare className="h-4 w-4" />
                      {post.comment_count || 0} yorum
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setWallModalOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İş Duvarı Paylaşım Detay Modal */}
      <Dialog open={wallDetailModalOpen} onOpenChange={setWallDetailModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="wall-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <StickyNote className="h-5 w-5" />
              Paylaşım Detayı
            </DialogTitle>
          </DialogHeader>
          
          {selectedWallPost && (
            <>
              {/* Ana Paylaşım */}
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    <div className="h-10 w-10 rounded-full bg-amber-200 flex items-center justify-center text-amber-800 font-semibold">
                      {selectedWallPost.created_by_name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="font-medium">{selectedWallPost.created_by_name}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(selectedWallPost.created_at).toLocaleString('tr-TR')}
                      </p>
                    </div>
                  </div>
                  {selectedWallPost.is_owner && !isJobReadOnly && (
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); openEditWallPostModal(selectedWallPost); }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                        onClick={(e) => { e.stopPropagation(); handleDeleteWallPost(selectedWallPost.id); }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                <p className="text-gray-800 whitespace-pre-wrap text-lg">{selectedWallPost.content}</p>
                
                {/* Belgeler */}
                {selectedWallPost.documents?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {selectedWallPost.documents.map((doc, idx) => (
                      <a
                        key={idx}
                        href={doc.data || doc.url}
                        download={doc.name}
                        className="flex items-center gap-1 px-2 py-1 bg-white rounded border text-sm hover:bg-gray-50"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Download className="h-3 w-3" />
                        {doc.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Yorumlar */}
              <div className="mt-4">
                <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Yorumlar ({selectedWallPost.comments?.length || 0})
                </h4>
                
                <div className="space-y-3 max-h-[250px] overflow-y-auto">
                  {selectedWallPost.comments?.map(comment => (
                    <div key={comment.id} className="bg-gray-50 p-3 rounded-lg border">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <div className="h-7 w-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-semibold text-xs">
                            {comment.created_by_name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{comment.created_by_name}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(comment.created_at).toLocaleString('tr-TR')}
                            </p>
                          </div>
                        </div>
                        {comment.is_owner && !isJobReadOnly && (
                          <div className="flex gap-1">
                            <button 
                              onClick={() => openEditWallCommentModal(comment)}
                              className="text-gray-400 hover:text-blue-600 p-1"
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                            <button 
                              onClick={() => handleDeleteWallComment(comment.id)}
                              className="text-gray-400 hover:text-red-600 p-1"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                      <p className="text-gray-700 text-sm whitespace-pre-wrap">{comment.content}</p>
                      {comment.documents?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {comment.documents.map((doc, idx) => (
                            <a
                              key={idx}
                              href={doc.data || doc.url}
                              download={doc.name}
                              className="flex items-center gap-1 px-2 py-0.5 bg-white rounded text-xs border hover:bg-gray-100"
                            >
                              <Paperclip className="h-3 w-3" />
                              {doc.name}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {(!selectedWallPost.comments || selectedWallPost.comments.length === 0) && (
                  <p className="text-center text-gray-400 py-4 text-sm">Henüz yorum yok</p>
                )}
              </div>

              {/* Yorum Ekleme Formu - Salt okunur modda gizle */}
              {!isJobReadOnly && (
              <div className="mt-4 pt-4 border-t">
                <Label className="text-sm font-medium mb-2 block">Yorum Ekle</Label>
                <Textarea
                  value={wallCommentContent}
                  onChange={(e) => setWallCommentContent(e.target.value)}
                  placeholder="Yorumunuzu yazın..."
                  rows={2}
                  className="mb-2"
                  data-testid="wall-comment-input"
                />
                
                {/* Belge yükleme */}
                <div className="mb-3">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => handleWallFileUpload(e, true)}
                    className="hidden"
                    id="branch-wall-comment-file"
                  />
                  <label 
                    htmlFor="branch-wall-comment-file" 
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs border rounded cursor-pointer hover:bg-gray-50"
                  >
                    <Paperclip className="h-3 w-3" />
                    Belge Ekle
                  </label>
                  {wallCommentDocuments.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {wallCommentDocuments.map((doc, idx) => (
                        <div key={idx} className="flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded text-xs">
                          <span className="truncate max-w-[100px]">{doc.name}</span>
                          <button 
                            onClick={() => setWallCommentDocuments(prev => prev.filter((_, i) => i !== idx))}
                            className="text-red-500"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <Button 
                  onClick={handleAddWallComment} 
                  disabled={wallSubmitting || !wallCommentContent.trim()}
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="add-wall-comment-btn"
                >
                  {wallSubmitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                  Yorum Ekle
                </Button>
              </div>
              )}
            </>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setWallDetailModalOpen(false); setSelectedWallPost(null); }}>
              Geri Dön
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İş Duvarı Paylaşım Düzenleme Modal */}
      <Dialog open={editWallPostModalOpen} onOpenChange={setEditWallPostModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-600" />
              Paylaşımı Düzenle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>İçerik *</Label>
              <Textarea
                value={wallPostContent}
                onChange={(e) => setWallPostContent(e.target.value)}
                placeholder="Paylaşım içeriği..."
                rows={4}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Belgeler</Label>
              <input
                type="file"
                multiple
                onChange={(e) => handleWallFileUpload(e, false)}
                className="mt-1 w-full text-sm"
              />
              {wallPostDocuments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {wallPostDocuments.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                      <span className="truncate">{doc.name}</span>
                      <button onClick={() => setWallPostDocuments(prev => prev.filter((_, i) => i !== idx))}>
                        <X className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditWallPostModalOpen(false); setEditingWallPost(null); setWallPostContent(''); setWallPostDocuments([]); }}>
              İptal
            </Button>
            <Button onClick={handleUpdateWallPost} disabled={wallSubmitting} className="bg-blue-600 hover:bg-blue-700">
              {wallSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Pencil className="h-4 w-4 mr-2" />}
              Güncelle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İş Duvarı Yorum Düzenleme Modal */}
      <Dialog open={editWallCommentModalOpen} onOpenChange={setEditWallCommentModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-600" />
              Yorumu Düzenle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Yorum *</Label>
              <Textarea
                value={wallCommentContent}
                onChange={(e) => setWallCommentContent(e.target.value)}
                placeholder="Yorum içeriği..."
                rows={3}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Belgeler</Label>
              <input
                type="file"
                multiple
                onChange={(e) => handleWallFileUpload(e, true)}
                className="mt-1 w-full text-sm"
              />
              {wallCommentDocuments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {wallCommentDocuments.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                      <span className="truncate">{doc.name}</span>
                      <button onClick={() => setWallCommentDocuments(prev => prev.filter((_, i) => i !== idx))}>
                        <X className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditWallCommentModalOpen(false); setEditingWallComment(null); setWallCommentContent(''); setWallCommentDocuments([]); }}>
              İptal
            </Button>
            <Button onClick={handleUpdateWallComment} disabled={wallSubmitting} className="bg-blue-600 hover:bg-blue-700">
              {wallSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Pencil className="h-4 w-4 mr-2" />}
              Güncelle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İş İptal Modalı */}
      {/* İşi Tamamla Modal */}
      <Dialog open={completeModalOpen} onOpenChange={setCompleteModalOpen}>
        <DialogContent className="max-w-md" data-testid="complete-job-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              İşi Tamamla
            </DialogTitle>
            <DialogDescription>
              "{selectedJob?.name || selectedJob?.konu || selectedJob?.amac}" işini tamamlamak üzeresiniz.
              Lütfen tamamlama açıklaması yazın. Bu açıklamayı işi takip eden herkes görecektir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Tamamlama Açıklaması <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={completeNote}
                onChange={(e) => setCompleteNote(e.target.value)}
                placeholder="İş nasıl tamamlandı? Detaylı açıklama yazın..."
                rows={4}
                className="w-full"
                data-testid="complete-note-input"
              />
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-700">
                <strong>Bilgi:</strong> Tamamlanan iş "Tamamlanan İşler" sekmesine taşınır ve işi takip eden
                tüm kişiler için tamamlanmış olarak görünür; açıklamayı da görebilirler.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setCompleteModalOpen(false); setCompleteNote(''); }}
            >
              Vazgeç
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={handleCompleteJob}
              disabled={completingJob || !completeNote.trim()}
              data-testid="confirm-complete-btn"
            >
              {completingJob ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Tamamlanıyor...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  İşi Tamamla
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="h-5 w-5" />
              İşi İptal Et
            </DialogTitle>
            <DialogDescription>
              "{selectedJob?.name || selectedJob?.konu || selectedJob?.amac}" işini iptal etmek üzeresiniz. 
              Lütfen iptal nedenini açıklayın.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                İptal Açıklaması <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Bu işi neden iptal ediyorsunuz? Detaylı açıklama yazın..."
                rows={4}
                className="w-full"
                data-testid="cancel-reason-input"
              />
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">
                <strong>Uyarı:</strong> İptal edilen işler "İptal Edilenler" sekmesine taşınacaktır. 
                Bu işlem geri alınabilir.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setCancelModalOpen(false); setCancelReason(''); }}
            >
              Vazgeç
            </Button>
            <Button 
              variant="destructive"
              onClick={handleCancelJob}
              disabled={cancellingJob || !cancelReason.trim()}
              data-testid="confirm-cancel-btn"
            >
              {cancellingJob ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  İptal Ediliyor...
                </>
              ) : (
                <>
                  <Ban className="h-4 w-4 mr-2" />
                  İşi İptal Et
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ara Verme Sebebi Modal */}
      <Dialog open={pauseModalOpen} onOpenChange={setPauseModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              İşe Ara Ver
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {jobToPause && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-500">İş</p>
                <p className="font-semibold">{jobToPause.name}</p>
              </div>
            )}

            <div>
              <Label htmlFor="pause-reason">Ara Verme Sebebi *</Label>
              <Textarea
                id="pause-reason"
                placeholder="Neden bu işe ara veriyorsunuz?"
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
                className="mt-1"
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPauseModalOpen(false)} disabled={pauseLoading}>
              İptal
            </Button>
            <Button 
              onClick={handlePauseJob}
              className="bg-yellow-600 hover:bg-yellow-700 text-white gap-2"
              disabled={pauseLoading}
            >
              {pauseLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  İşleniyor...
                </>
              ) : (
                <>
                  <AlertCircle className="h-4 w-4" />
                  Ara Ver
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchJobs;
